import { useState, useEffect } from "react";
// ── DEFAULT CONFIG ────────────────────────────────────────────────────────────
const DEFAULT_CONFIG = {
 brand: {
 name: "DRIVE",
 nameAccent: ".AI",
 tagline: "Commercial Vehicle Sales Platform",
 primaryColor: "#FC4D16",
 secondaryColor: "#064E60",
 accentColor: "#771735",
 highlightColor: "#D9E571",
 infoColor: "#B7D9FF",
 bgColor: "#053347",
 navBg: "#771735",
 logoText: "ncla",
 },
 workflow: {
 phases: [
 "Order Confirmed","Build Slot","Production","In Transit","Pre-Delivery","Handover"
 ],
 tasks: [
 { phase:"Order Confirmed", name:"Log signed order in CRM", type:"action", detail:"Upload the signed order to your CRM. Ensure customer details, vehicle spec, pricing and discount are all recorded accurately." },
 { phase:"Order Confirmed", name:"Countersign the customer order", type:"action", detail:"Ensure the order is countersigned by the appropriate authorised signatory before processing." },
 { phase:"Order Confirmed", name:"Submit deal for manager approval", type:"action", detail:"Submit the deal summary including vehicle spec, discount level and commission for manager sign-off." },
 { phase:"Order Confirmed", name:"Manager approval notification sent automatically", type:"auto", detail:"A pre-populated approval summary is sent to your manager automatically. No further action needed." },
 { phase:"Order Confirmed", name:"Place order with manufacturer or dealer", type:"action", detail:"Place the confirmed order through the appropriate channel — manufacturer portal, dealer or internal system." },
 { phase:"Order Confirmed", name:"Deposit invoice sent to customer automatically", type:"auto", detail:"Deposit invoice sent to customer from your email with a personalised covering message. You are copied." },
 { phase:"Build Slot", name:"Monitor order for build slot confirmation", type:"action", detail:"Check your order bank or manufacturer portal regularly. When a build slot is confirmed, enter the date to trigger customer notification." },
 { phase:"Build Slot", name:"Build slot confirmation sent automatically", type:"auto", detail:"Triggered when you enter the confirmed build slot date. Sent from your email confirming the expected timeline." },
 { phase:"Build Slot", name:"Build slot change notification sent automatically", type:"auto", detail:"If you update the build slot date, the customer is notified automatically. You are copied." },
 { phase:"Build Slot", name:"Confirm final specification with manufacturer", type:"action", detail:"Verify final specification confirmation. Flag any discrepancies with the manufacturer immediately." },
 { phase:"Production", name:"Monitor production progress", type:"action", detail:"Track the vehicle through production. Update expected completion date if advised of any delays." },
 { phase:"Production", name:"Confirm pre-delivery requirements with customer", type:"action", detail:"Confirm: invoice instructions, delivery or collection preference, delivery address if applicable." },
 { phase:"Production", name:"Commission logged automatically", type:"auto", detail:"Commission calculated from deal data and recorded automatically. Visible in your commission tracker." },
 { phase:"Production", name:"Arrange PDI with dealer or PDI centre", type:"action", detail:"Book the pre-delivery inspection. Confirm scope, timing and any additional requirements." },
 { phase:"Production", name:"PDI purchase order sent automatically", type:"auto", detail:"PDI purchase order sent directly to the PDI centre from your email. You are copied." },
 { phase:"In Transit", name:"Mark vehicle as in transit", type:"action", detail:"When you receive confirmation the vehicle has left the factory, mark it as in transit. This triggers the customer notification sequence." },
 { phase:"In Transit", name:"In-transit update sent to customer automatically", type:"auto", detail:"Customer receives a personalised email confirming the vehicle is in transit with an estimated arrival window." },
 { phase:"In Transit", name:"Confirm final invoice details with customer", type:"action", detail:"Confirm billing address, VAT number and any purchase order reference required." },
 { phase:"In Transit", name:"Invoice sent to customer automatically", type:"auto", detail:"Final invoice sent from your email. Payment chase sequence starts automatically if payment is not received within agreed terms." },
 { phase:"In Transit", name:"Confirm delivery or collection arrangements", type:"action", detail:"Confirm final delivery arrangements — transport booking, delivery address, or collection slot." },
 { phase:"Pre-Delivery", name:"PDI completed — confirm quality check", type:"action", detail:"Confirm the PDI has been completed to specification. Any snags should be resolved before handover." },
 { phase:"Pre-Delivery", name:"Arrange accessories or additional fitments", type:"action", detail:"Coordinate any accessories, signwriting, body modifications or equipment fitment required before handover." },
 { phase:"Pre-Delivery", name:"Confirm vehicle movement to handover location", type:"action", detail:"Arrange and confirm movement of the vehicle to the handover location." },
 { phase:"Pre-Delivery", name:"Handover confirmation sent to customer automatically", type:"auto", detail:"Customer notified automatically with confirmed handover date, time and location. You are copied." },
 { phase:"Pre-Delivery", name:"Payment chase sequence running automatically", type:"auto", detail:"If invoice unpaid, escalating payment reminders are sent at agreed intervals. You are notified at each stage." },
 { phase:"Pre-Delivery", name:"Confirm all documentation is ready", type:"action", detail:"Ensure ready: registration document, warranty documentation, service book, keys, user manuals and certificates." },
 { phase:"Handover", name:"Conduct vehicle handover", type:"action", detail:"Walk the customer through the vehicle — controls, safety features, telematics, fuel type and operational requirements." },
 { phase:"Handover", name:"Customer signs delivery note", type:"action", detail:"Obtain the customer signature on the delivery note confirming receipt in satisfactory condition." },
 { phase:"Handover", name:"Submit signed delivery note", type:"action", detail:"Submit the signed delivery note to the relevant party. This typically triggers your commission payment." },
 { phase:"Handover", name:"Thank you message sent to customer automatically", type:"auto", detail:"A personalised thank you sent from your address within 24 hours of handover. Starts the post-handover relationship sequence." },
 { phase:"Handover", name:"Deal complete — commission payment logged", type:"watch", detail:"Deal confirmed complete and expected commission payment date logged. Customer moves into post-handover relationship sequence." },
 ]
 },
 commission: {
 columns: ["Chassis","Finance","R&M","Warranty"],
 funders: ["Outright Purchase","Finance","Contract Hire","Lease","Other"],
 statuses: ["Pipeline","Confirmed","Paid"],
 },
 targets: {
 labels: ["Handovers","Order Intake","Revenue","Appointments"],
 annual: [48, 60, 1200000, 240],
 },
 access: {
 appPassword: "drive2024",
 adminPassword: "admin2024",
 },
 email: {
 provider: "outlook",
 fromName: "",
 fromEmail: "",
 msClientId: "",
 msTenantId: "",
 configured: false,
 },
 emailTemplates: {
 depositInvoice: { subject:"Your order has been confirmed — {vehicleSpec}", body:"Dear {customerName},\n\nThank you for your order. I am pleased to confirm your {vehicleSpec} has been successfully registered on our system.\n\nPlease find attached your deposit invoice. Once this has been settled we will proceed with placing your order.\n\nIf you have any questions please don't hesitate to get in touch.\n\nKind regards,\n{senderName}" },
 buildSlot: { subject:"Build slot confirmed — {vehicleSpec}", body:"Dear {customerName},\n\nI am pleased to let you know that a build slot has been confirmed for your {vehicleSpec}.\n\nExpected build date: {buildDate}\n\nI will keep you updated as your vehicle progresses through production. In the meantime please don't hesitate to contact me if you need anything.\n\nKind regards,\n{senderName}" },
 inTransit: { subject:"Your {vehicleSpec} is on its way", body:"Dear {customerName},\n\nGreat news — your {vehicleSpec} has left the factory and is now in transit.\n\nExpected arrival: {eta}\n\nI will be in touch shortly to confirm final delivery arrangements. Please can you also confirm your invoice instructions and preferred delivery address at your earliest convenience.\n\nKind regards,\n{senderName}" },
 handoverConfirmation: { subject:"Handover confirmed — {vehicleSpec}", body:"Dear {customerName},\n\nI am looking forward to handing over your {vehicleSpec}.\n\nHandover details:\nDate: {handoverDate}\nTime: {handoverTime}\nLocation: {handoverLocation}\n\nIf you need to make any changes please contact me as soon as possible.\n\nKind regards,\n{senderName}" },
 thankYou: { subject:"Thank you — {vehicleSpec} handover", body:"Dear {customerName},\n\nThank you for collecting your {vehicleSpec} today. It was a pleasure handing over the vehicle and I hope you are delighted with it.\n\nI will be in touch in the coming weeks to make sure everything is going well. In the meantime please don't hesitate to contact me if you need anything at all.\n\nKind regards,\n{senderName}" },
 paymentChase1: { subject:"Invoice reminder — {vehicleSpec}", body:"Dear {customerName},\n\nI hope you are well. I wanted to follow up on the invoice for your {vehicleSpec} which was due for payment on {dueDate}.\n\nIf payment has already been made please disregard this message. If you have any queries regarding the invoice please don't hesitate to get in touch.\n\nKind regards,\n{senderName}" },
 rel1week: { subject:"Checking in — your {vehicleSpec}", body:"Dear {customerName},\n\nI hope you are settling in well with your new {vehicleSpec}. It has been a week since handover and I wanted to check everything is going smoothly.\n\nIf you have any questions or need any support please don't hesitate to contact me directly.\n\nKind regards,\n{senderName}" },
 rel1month: { subject:"One month check-in — {vehicleSpec}", body:"Dear {customerName},\n\nI hope your {vehicleSpec} is performing well. It has been a month since handover and I wanted to touch base to make sure you are completely satisfied.\n\nIf there is anything I can help with — servicing, accessories, additional vehicles — please don't hesitate to get in touch.\n\nKind regards,\n{senderName}" },
 rel3month: { subject:"Three month review — {vehicleSpec}", body:"Dear {customerName},\n\nI hope all is well. It has been three months since you took delivery of your {vehicleSpec} and I wanted to check in.\n\nI would love to hear how the vehicle is performing for your business. If you have any feedback or requirements please feel free to contact me.\n\nKind regards,\n{senderName}" },
 rel12month: { subject:"Annual check-in — your fleet", body:"Dear {customerName},\n\nI hope you are well. It has been a year since you took delivery of your {vehicleSpec} and I wanted to touch base.\n\nI would be delighted to discuss any upcoming vehicle requirements or fleet changes you may have planned. Please don't hesitate to get in touch.\n\nKind regards,\n{senderName}" },
 rel24month: { subject:"Fleet review — upcoming requirements", body:"Dear {customerName},\n\nI hope all is well. It has been two years since your last vehicle and I wanted to reach out to discuss any upcoming fleet requirements.\n\nWith lead times as they are it is well worth planning ahead. I would welcome the opportunity to discuss your needs.\n\nKind regards,\n{senderName}" },
 rel36month: { subject:"Vehicle renewal — let's talk", body:"Dear {customerName},\n\nI hope you are well. Your {vehicleSpec} is now three years old and approaching a natural renewal point.\n\nI would love to discuss the latest models and what might work best for your business. Would you be open to a conversation in the coming weeks?\n\nKind regards,\n{senderName}" },
 },
 prospecting: {
 targetPostcodes: [],
 monitorNewCompanies: true,
 monitorOLicences: true,
 monitorVehicleRegs: true,
 }
};
// ── STORAGE ───────────────────────────────────────────────────────────────────
function loadData(key, def) { try { const d=localStorage.getItem(key); return d?JSON.parse(d):def; } catch { return def; } }
function saveData(key, val) { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} }
// ── UTILS ─────────────────────────────────────────────────────────────────────
const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2,6);
const fmt = v => v ? `£${Number(v).toLocaleString("en-GB",{minimumFractionDigits:2,maximumFractionDigits:2})}` : "—";
const num = v => parseFloat(v)||0;
const getProgress = tasks => (tasks&&tasks.length) ? Math.round(tasks.filter(t=>t.status==="Complete").length/tasks.length*100) : 0;
const getBlocked = tasks => tasks ? tasks.filter(t=>t.status==="Blocked").length : 0;
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
// SIC codes commonly associated with operators likely to run trucks/vans
const FLEET_LIKELY_SIC = ["49410","49420","52100","52210","52290","43120","81210","49390"];
// ── EMAIL ─────────────────────────────────────────────────────────────────────
function fillTemplate(template, vars) {
 return Object.entries(vars).reduce((s,[k,v])=>s.replaceAll(`{${k}}`,v||""),template);
}
async function sendEmail(cfg, to, subject, body) {
 const { msClientId, configured } = cfg.email || {};
 if(!configured || !msClientId) {
 // Fallback — copy to clipboard
 const text = `To: ${to}\nSubject: ${subject}\n\n${body}`;
 try { await navigator.clipboard.writeText(text); } catch {}
 return { sent: false, copied: true };
 }
 try {
 // Microsoft Graph — requires token from Azure AD
 // In production this uses MSAL.js — for now returns clipboard fallback
 // Full OAuth flow requires Azure app registration
 const text = `To: ${to}\nSubject: ${subject}\n\n${body}`;
 await navigator.clipboard.writeText(text);
 return { sent: false, copied: true, message: "Email credentials not yet configured — content copied to clipboard" };
 } catch {
 return { sent: false, copied: false };
 }
}
const TOUCHPOINTS = [
 { key:"rel1week", label:"1 Week", days:7 },
 { key:"rel1month", label:"1 Month", days:30 },
 { key:"rel3month", label:"3 Months", days:90 },
 { key:"rel12month",label:"12 Months", days:365 },
 { key:"rel24month",label:"24 Months", days:730 },
 { key:"rel36month",label:"36 Months", days:1095},
];
function AppStyles({ cfg }) {
 const p = cfg.brand.primaryColor;
 const s = cfg.brand.secondaryColor;
 const a = cfg.brand.accentColor;
 const h = cfg.brand.highlightColor;
 const info = cfg.brand.infoColor;
 const bg = cfg.brand.bgColor;
 const nav = cfg.brand.navBg;
 const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&family=Special+Gothic+Expanded+One&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
body{font-family:'DM Sans',sans-serif;background:${bg};color:#e8eff5;margin:0;min-height:100vh;}
.app{min-height:100vh;display:flex;flex-direction:column;}
/* NAV */
.nav{background:${nav};padding:0 28px;height:68px;display:flex;align-items:center;justify-content:space-between;border-bottom:4px solid ${p};position:sticky;top:0;z-index:100;box-shadow:0 4px 32px rgba(0,0,0,.4);}
.nav-left{display:flex;align-items:center;gap:16px;}
.nav-product{display:flex;flex-direction:column;justify-content:center;}
.nav-name{font-family:'Special Gothic Expanded One',sans-serif;font-size:19px;color:#fff;letter-spacing:.08em;line-height:1.1;}
.nav-accent{font-family:'Special Gothic Expanded One',sans-serif;font-size:19px;color:${p};letter-spacing:.08em;}
.nav-tagline{font-size:9px;color:rgba(255,245,233,.5);letter-spacing:.16em;margin-top:3px;text-transform:uppercase;font-weight:600;}
.nav-right{display:flex;align-items:center;gap:20px;}
.nav-stat{font-size:11px;color:rgba(255,245,233,.45);display:flex;align-items:center;gap:5px;}
.nav-stat strong{color:rgba(255,245,233,.85);}
/* TABS */
.app-tabs{background:color-mix(in srgb,${s} 25%,#000);border-bottom:1px solid ${s}cc;display:flex;gap:0;padding:0 28px;overflow-x:auto;}
.app-tab{font-family:'Special Gothic Expanded One',sans-serif;font-size:10px;letter-spacing:.08em;padding:15px 22px;color:${info}55;border:none;background:transparent;cursor:pointer;border-bottom:3px solid transparent;transition:all .2s;white-space:nowrap;}
.app-tab.active{color:#FFF5E9;border-bottom-color:${p};background:${p}0a;}
.app-tab:hover:not(.active){color:${info}99;}
/* MAIN */
.main{flex:1;padding:32px 28px;max-width:1200px;margin:0 auto;width:100%;}
/* BUTTONS */
.btn{font-family:'DM Sans',sans-serif;font-size:12px;font-weight:700;padding:10px 22px;border:none;border-radius:100px;cursor:pointer;transition:all .18s;display:inline-flex;align-items:center;gap:6px;letter-spacing:.04em;text-transform:uppercase;}
.btn-primary{background:${p};color:#fff;}
.btn-primary:hover{filter:brightness(1.1);transform:translateY(-1px);box-shadow:0 6px 20px ${p}66;}
.btn-secondary{background:${a}55;color:${info};border:1px solid ${a}99;}
.btn-secondary:hover{background:${a}88;}
.btn-ghost{background:transparent;color:${info}55;padding:5px 14px;font-size:11px;border-radius:100px;text-transform:none;}
.btn-ghost:hover{color:${info};}
.btn-sm{padding:5px 14px;font-size:11px;}
.btn-danger{background:${a}44;color:#FFB7B8;border:1px solid ${a}88;}
.btn-danger:hover{background:${a}66;}
.btn:disabled{opacity:.3;cursor:not-allowed;transform:none!important;}
/* SCREEN HEADER */
.sh{display:flex;align-items:flex-end;justify-content:space-between;margin-bottom:32px;flex-wrap:wrap;gap:12px;padding-bottom:20px;border-bottom:1px solid ${info}14;}
.sh-title{font-family:'Special Gothic Expanded One',sans-serif;font-size:22px;color:#FFF5E9;letter-spacing:.05em;}
.sh-sub{font-size:10px;color:${info}55;margin-top:6px;text-transform:uppercase;letter-spacing:.14em;font-weight:600;}
/* STATS */
.stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(130px,1fr));gap:14px;margin-bottom:32px;}
.stat{background:${a}33;border:1px solid ${a}66;border-top:3px solid ${a};border-radius:12px;padding:20px 18px;}
.stat-lbl{font-size:9px;color:#FFB7B8aa;text-transform:uppercase;letter-spacing:.14em;margin-bottom:10px;font-weight:700;}
.stat-val{font-family:'Special Gothic Expanded One',sans-serif;font-size:30px;color:#FFF5E9;}
.stat-val.red{color:${p};}
.stat-val.grn{color:${h};}
.stat-val.blu{color:${info};}
/* CARDS */
.grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(320px,1fr));gap:18px;}
.card{background:${s}22;border:1px solid ${s}bb;border-radius:16px;padding:22px;cursor:pointer;transition:all .2s;position:relative;overflow:hidden;}
.card::after{content:'';position:absolute;top:0;left:0;right:0;height:4px;background:linear-gradient(90deg,${p},${a});}
.card.blk{border-color:${a}cc;}
.card.blk::after{background:${a};}
.card.done{border-color:${h}55;}
.card.done::after{background:linear-gradient(90deg,${h},#a8b840);}
.card:hover{background:${s}44;transform:translateY(-4px);box-shadow:0 12px 40px #0008;border-color:${info}44;}
.c-top{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:12px;}
.c-name{font-family:'Special Gothic Expanded One',sans-serif;font-size:13px;color:#FFF5E9;letter-spacing:.03em;}
.c-co{font-size:11px;color:${info}55;margin-top:3px;}
.c-veh{font-size:12px;color:${info};margin:10px 0;font-weight:600;letter-spacing:.04em;}
.pill{font-size:9px;font-weight:700;padding:3px 10px;border-radius:100px;white-space:nowrap;letter-spacing:.05em;text-transform:uppercase;}
.pill-phase{background:${s}aa;color:${info};border:1px solid ${info}44;}
.pill-multi{background:${a}66;color:#FFB7B8;border:1px solid #FFB7B844;}
.prog-bar{margin:14px 0 10px;}
.prog-labels{display:flex;justify-content:space-between;font-size:10px;color:${info}44;margin-bottom:7px;}
.prog-track{height:3px;background:#fff1;border-radius:2px;}
.prog-fill{height:3px;border-radius:2px;background:linear-gradient(90deg,${p},${a});transition:width .4s;}
.prog-fill.done{background:linear-gradient(90deg,${h},#a8b840);}
.c-foot{display:flex;gap:6px;flex-wrap:wrap;margin-top:14px;align-items:center;}
.tag{font-size:9px;font-weight:700;padding:3px 9px;border-radius:100px;letter-spacing:.04em;text-transform:uppercase;}
.tag-blk{background:${a}44;color:#FFB7B8;}
.tag-ok{background:${h}1a;color:${h};}
.tag-action{background:${p}22;color:${p};}
/* EMPTY */
.empty{text-align:center;padding:100px 24px;}
.empty-icon{font-size:56px;margin-bottom:24px;opacity:.15;}
.empty-ttl{font-family:'Special Gothic Expanded One',sans-serif;font-size:16px;color:${info}44;margin-bottom:10px;letter-spacing:.06em;}
/* MODAL */
.overlay{position:fixed;inset:0;background:#000e;backdrop-filter:blur(8px);z-index:200;display:flex;align-items:center;justify-content:center;padding:18px;animation:fi .14s;}
@keyframes fi{from{opacity:0}to{opacity:1}}
.modal{background:color-mix(in srgb,${s} 15%,#03111a);border:1px solid ${s}dd;border-top:4px solid ${p};border-radius:18px;width:100%;max-width:580px;max-height:92vh;overflow-y:auto;animation:su .2s;}
@keyframes su{from{transform:translateY(16px);opacity:0}to{transform:none;opacity:1}}
.mh{padding:24px 24px 0;display:flex;justify-content:space-between;align-items:center;margin-bottom:20px;}
.mt{font-family:'Special Gothic Expanded One',sans-serif;font-size:15px;color:#FFF5E9;letter-spacing:.05em;}
.mb{padding:0 24px 24px;}
.fg{margin-bottom:16px;}
.fl{display:block;font-size:9px;font-weight:700;color:${info}55;margin-bottom:7px;text-transform:uppercase;letter-spacing:.14em;}
.fi,.fs,.fta{width:100%;background:${s}22;border:1px solid ${s}99;border-radius:10px;padding:10px 15px;font-family:'DM Sans',sans-serif;font-size:13px;color:#e8eff5;outline:none;transition:border-color .15s,box-shadow .15s;}
.fi:focus,.fs:focus,.fta:focus{border-color:${info};box-shadow:0 0 0 3px ${info}1a;}
.fs option{background:#03111a;}
.fta{resize:vertical;min-height:72px;}
.fr{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
.ma{display:flex;gap:10px;justify-content:flex-end;margin-top:22px;}
.mode-pick{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:4px;}
.mode-card{border:2px solid ${s}66;border-radius:14px;padding:16px;cursor:pointer;transition:all .15s;text-align:center;background:${s}1a;}
.mode-card:hover{border-color:${info}44;background:${s}33;}
.mode-card.sel{border-color:${p};background:${p}14;}
.mode-icon{font-size:24px;margin-bottom:6px;}
.mode-label{font-family:'Special Gothic Expanded One',sans-serif;font-size:11px;color:#FFF5E9;letter-spacing:.04em;}
.mode-desc{font-size:10px;color:${info}44;margin-top:4px;}
/* DEAL DETAIL */
.back-btn{font-family:'DM Sans',sans-serif;font-size:11px;font-weight:700;color:${info}55;background:none;border:none;cursor:pointer;padding:0 0 20px;display:flex;align-items:center;gap:6px;letter-spacing:.05em;text-transform:uppercase;}
.back-btn:hover{color:${info};}
.dhdr{background:${a}28;border:1px solid ${a}55;border-left:4px solid ${a};border-radius:14px;padding:24px;margin-bottom:24px;}
.dh-row{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;flex-wrap:wrap;}
.dh-name{font-family:'Special Gothic Expanded One',sans-serif;font-size:20px;color:#FFF5E9;letter-spacing:.04em;margin-bottom:5px;}
.dh-co{font-size:12px;color:#FFB7B866;margin-bottom:8px;}
.dh-veh{font-size:14px;color:${info};font-weight:600;margin-bottom:12px;letter-spacing:.04em;}
.dh-meta{display:flex;flex-direction:column;gap:5px;}
.mi{font-size:12px;color:#FFB7B866;}
.mi strong{color:#FFB7B8aa;}
.dh-right{display:flex;flex-direction:column;align-items:flex-end;gap:10px;}
.phase-hdr{font-family:'Special Gothic Expanded One',sans-serif;font-size:11px;color:${info};letter-spacing:.1em;margin:24px 0 12px;padding-bottom:10px;border-bottom:1px solid ${s}88;text-transform:uppercase;display:flex;align-items:center;gap:8px;}
.phase-hdr::before{content:'';display:block;width:3px;height:14px;background:${p};border-radius:2px;flex-shrink:0;}
/* TASKS */
.trow{border:1px solid ${s}55;background:${s}18;border-radius:10px;margin-bottom:6px;transition:all .15s;overflow:hidden;}
.trow:hover{border-color:${info}2a;background:${s}30;}
.trow.done{opacity:.33;}
.trow.blk{border-color:${a}88;background:${a}18;}
.trow-main{display:grid;grid-template-columns:1fr auto;gap:12px;align-items:center;padding:13px 16px;cursor:pointer;}
.trow-left{display:flex;align-items:flex-start;gap:12px;}
.type-bar{width:3px;border-radius:2px;align-self:stretch;flex-shrink:0;margin-top:2px;}
.chk{width:18px;height:18px;border-radius:5px;border:2px solid ${info}33;cursor:pointer;flex-shrink:0;margin-top:1px;display:flex;align-items:center;justify-content:center;transition:all .15s;background:transparent;font-size:10px;color:#fff;}
.chk.on{background:${s};border-color:${info};}
.chk.auto-chk{border-color:${h}44;background:${h}0f;cursor:default;}
.chk.watch-chk{border-color:${info}2a;background:${info}0a;cursor:default;}
.tinfo{flex:1;min-width:0;}
.tname{font-size:13px;color:#ccdde8;line-height:1.4;}
.tname.done{text-decoration:line-through;color:${info}2a;}
.tmeta{display:flex;gap:8px;margin-top:5px;flex-wrap:wrap;align-items:center;}
.type-pill{font-size:9px;font-weight:700;padding:2px 8px;border-radius:100px;letter-spacing:.04em;text-transform:uppercase;}
.due-tag{font-size:10px;color:${info}44;}
.note-txt{font-size:11px;color:${info}33;margin-top:3px;font-style:italic;cursor:pointer;}
.note-row{display:flex;gap:5px;margin-top:5px;}
.note-row .fi{font-size:12px;padding:5px 10px;}
.trow-right{display:flex;align-items:center;gap:8px;flex-shrink:0;}
.date-in{font-size:11px;padding:5px 12px;border-radius:100px;background:${s}44;border:1px solid ${s}aa;color:${info};font-family:'DM Sans',sans-serif;width:116px;outline:none;}
.ssel{font-size:11px;font-weight:700;padding:5px 12px;border-radius:100px;border:none;cursor:pointer;font-family:'DM Sans',sans-serif;outline:none;letter-spacing:.03em;}
.detail-panel{background:${s}14;border-top:1px solid ${s}44;padding:14px 16px 16px 46px;}
.dp-detail{font-size:12px;color:${info}44;line-height:1.6;margin-bottom:8px;}
.dp-auto-note{font-size:11px;color:${h};display:flex;align-items:flex-start;gap:6px;margin-top:8px;padding:10px 14px;background:${h}0f;border-radius:8px;border:1px solid ${h}2a;}
.dp-watch-note{font-size:11px;color:${info};display:flex;align-items:flex-start;gap:6px;margin-top:8px;padding:10px 14px;background:${info}0a;border-radius:8px;border:1px solid ${info}22;}
.trigger-box{background:${p}0f;border:1px solid ${p}33;border-radius:10px;padding:16px;margin-top:10px;}
.trigger-lbl{font-family:'Special Gothic Expanded One',sans-serif;font-size:10px;color:${p};margin-bottom:10px;letter-spacing:.08em;text-transform:uppercase;}
.trigger-row{display:flex;gap:8px;align-items:center;flex-wrap:wrap;}
.trigger-input{font-size:12px;padding:8px 14px;border-radius:100px;background:${s}33;border:1px solid ${s}88;color:#e8eff5;font-family:'DM Sans',sans-serif;outline:none;}
.trigger-sent{font-size:11px;color:${h};margin-top:8px;font-weight:600;}
.comm-in{font-size:12px;padding:5px 12px;border-radius:100px;background:${s}33;border:1px solid ${s}77;color:#e8eff5;font-family:'DM Sans',sans-serif;width:128px;outline:none;}
.info-box{font-size:11px;color:${info}55;padding:12px 16px;background:${s}22;border-radius:10px;border:1px solid ${s}66;margin-bottom:16px;line-height:1.6;}
.info-box strong{color:${info}99;}
/* COMMISSION */
.comm-section{margin-bottom:36px;}
.comm-month-hdr{display:flex;align-items:center;justify-content:space-between;padding:14px 18px;background:${a}33;border:1px solid ${a}66;border-left:4px solid ${a};border-radius:10px;cursor:pointer;margin-bottom:2px;transition:all .15s;}
.comm-month-hdr:hover{background:${a}44;}
.comm-month-name{font-family:'Special Gothic Expanded One',sans-serif;font-size:12px;color:#FFF5E9;letter-spacing:.07em;}
.comm-month-total{font-family:'Special Gothic Expanded One',sans-serif;font-size:16px;color:${h};}
.comm-month-count{font-size:11px;color:#FFB7B844;}
.comm-table{width:100%;border-collapse:collapse;margin-bottom:4px;}
.comm-table th{font-size:9px;font-weight:700;color:${info}55;text-transform:uppercase;letter-spacing:.12em;padding:10px 12px;text-align:left;border-bottom:1px solid ${s}66;background:${s}14;}
.comm-table td{font-size:12px;color:#ccdde8;padding:9px 12px;border-bottom:1px solid ${s}2a;}
.comm-table tr:hover td{background:${s}22;}
.comm-input{font-size:12px;padding:5px 12px;border-radius:100px;background:${s}22;border:1px solid ${s}66;color:#e8eff5;font-family:'DM Sans',sans-serif;width:100%;outline:none;min-width:80px;transition:border-color .15s;}
.comm-input:focus{border-color:${info};}
.comm-total-row td{font-family:'Special Gothic Expanded One',sans-serif;font-size:13px;color:${h};background:${h}0a;border-top:2px solid ${h}33;padding:12px;}
.comm-status-pill{font-size:10px;font-weight:700;padding:4px 12px;border-radius:100px;border:none;cursor:pointer;font-family:'DM Sans',sans-serif;letter-spacing:.04em;text-transform:uppercase;transition:all .15s;}
.status-pipeline{background:${p}22;color:${p};}
.status-confirmed{background:${info}1a;color:${info};}
.status-paid{background:${h}1a;color:${h};}
.add-row-btn{font-size:11px;font-weight:600;color:${info}44;background:none;border:1px dashed ${s}66;cursor:pointer;padding:8px 16px;font-family:'DM Sans',sans-serif;border-radius:100px;transition:all .15s;margin-top:6px;}
.add-row-btn:hover{color:${info};border-color:${info}44;}
.year-total-box{background:${h}0f;border:1px solid ${h}33;border-left:4px solid ${h};border-radius:12px;padding:20px 24px;display:flex;gap:32px;align-items:center;flex-wrap:wrap;margin-top:10px;}
.yt-label{font-size:9px;color:${h}77;text-transform:uppercase;letter-spacing:.12em;margin-bottom:8px;font-weight:700;}
.yt-val{font-family:'Special Gothic Expanded One',sans-serif;font-size:28px;color:${h};}
/* TARGETS */
.target-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(210px,1fr));gap:16px;margin-bottom:32px;}
.target-card{background:${s}22;border:1px solid ${s}99;border-radius:14px;padding:22px;}
.tc-label{font-size:9px;color:${info}55;text-transform:uppercase;letter-spacing:.12em;margin-bottom:10px;font-weight:700;}
.tc-vals{display:flex;align-items:baseline;gap:10px;}
.tc-actual{font-family:'Special Gothic Expanded One',sans-serif;font-size:36px;}
.tc-actual.ahead{color:${h};}
.tc-actual.behind{color:${p};}
.tc-actual.ontrack{color:${info};}
.tc-target{font-size:13px;color:${info}44;}
.tc-bar{height:3px;background:${s}77;border-radius:2px;margin-top:14px;}
.tc-bar-fill{height:3px;border-radius:2px;transition:width .4s;}
.monthly-table{width:100%;border-collapse:collapse;}
.monthly-table th{font-size:9px;font-weight:700;color:${info}55;text-transform:uppercase;letter-spacing:.12em;padding:10px 12px;text-align:left;border-bottom:1px solid ${s}66;background:${s}14;}
.monthly-table td{font-size:12px;color:#ccdde8;padding:10px 12px;border-bottom:1px solid ${s}2a;}
.monthly-table tr:hover td{background:${s}18;}
.rag{font-size:10px;font-weight:700;padding:3px 10px;border-radius:100px;letter-spacing:.04em;text-transform:uppercase;}
.rag-green{background:${h}1a;color:${h};}
.rag-amber{background:${p}1a;color:${p};}
.rag-red{background:${a}33;color:#FFB7B8;}
.rag-none{background:${s}2a;color:${info}44;}
.target-input{font-size:12px;padding:5px 12px;border-radius:100px;background:${s}22;border:1px solid ${s}77;color:#e8eff5;font-family:'DM Sans',sans-serif;width:80px;outline:none;text-align:center;transition:border-color .15s;}
.target-input:focus{border-color:${info};}
/* SETUP WIZARD */
.wizard-overlay{position:fixed;inset:0;background:${bg};z-index:500;display:flex;align-items:center;justify-content:center;padding:24px;}
.wizard{background:color-mix(in srgb,${s} 12%,#03111a);border:1px solid ${s}cc;border-top:4px solid ${p};border-radius:20px;width:100%;max-width:640px;max-height:92vh;overflow-y:auto;}
.wizard-hdr{padding:32px 32px 0;text-align:center;}
.wizard-step{font-size:10px;color:${info}44;text-transform:uppercase;letter-spacing:.14em;margin-bottom:12px;font-weight:700;}
.wizard-title{font-family:'Special Gothic Expanded One',sans-serif;font-size:24px;color:#FFF5E9;letter-spacing:.04em;margin-bottom:8px;}
.wizard-sub{font-size:13px;color:${info}55;line-height:1.5;}
.wizard-body{padding:28px 32px;}
.wizard-foot{padding:0 32px 32px;display:flex;gap:12px;justify-content:flex-end;}
.progress-bar{height:3px;background:${s}44;margin:0 32px;border-radius:2px;}
.progress-fill{height:3px;background:${p};border-radius:2px;transition:width .4s;}
.color-row{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
.color-fg{margin-bottom:12px;}
.color-preview{width:32px;height:32px;border-radius:8px;border:2px solid #fff2;margin-top:6px;}
.color-input-row{display:flex;align-items:center;gap:10px;}
.color-swatch{width:36px;height:36px;border-radius:8px;border:none;cursor:pointer;padding:2px;}
.phase-list{display:flex;flex-direction:column;gap:8px;}
.phase-item{display:flex;align-items:center;gap:8px;}
.phase-item .fi{flex:1;}
.phase-num{font-family:'Special Gothic Expanded One',sans-serif;font-size:11px;color:${p};width:20px;text-align:center;}
.comm-col-list{display:flex;flex-wrap:wrap;gap:8px;}
.comm-col-chip{display:flex;align-items:center;gap:6px;background:${s}33;border:1px solid ${s}77;border-radius:100px;padding:6px 12px;}
.comm-col-chip input{background:transparent;border:none;color:#e8eff5;font-family:'DM Sans',sans-serif;font-size:12px;width:80px;outline:none;}
/* SCROLLBAR */
::-webkit-scrollbar{width:5px;}
::-webkit-scrollbar-track{background:${s}14;}
::-webkit-scrollbar-thumb{background:${a}77;border-radius:3px;}
@media(max-width:600px){
 .fr{grid-template-columns:1fr;}
 .dh-row{flex-direction:column;}
 .trow-main{grid-template-columns:1fr;}
 .trow-right{margin-top:8px;}
 .comm-table{font-size:11px;}
 .main{padding:20px 16px;}
 .nav{padding:0 16px;}
 .app-tabs{padding:0 16px;}
 .wizard{border-radius:16px;}
 .wizard-hdr{padding:24px 20px 0;}
 .wizard-body{padding:20px;}
 .wizard-foot{padding:0 20px 24px;}
}
`;
 return <style>{css}</style>;
}
// ── LOGO ──────────────────────────────────────────────────────────────────────
function BrandLogo({ cfg, size=38 }) {
 const { accentColor, infoColor } = cfg.brand;
 return (
 <svg height={size} viewBox="0 0 747.1 747.1" xmlns="http://www.w3.org/2000/svg">
 <circle fill={infoColor} cx="373.55" cy="373.55" r="373.55"/>
 <path fill={accentColor} d="M325.76,352.87v-62.41c0-15.2-5.27-22.29-17.63-22.29-13.58,0-19.25,7.5-19.25,22.7v62.01h-33.64v-112.47h31.41v11.96h.4c7.1-11.55,17.03-15.8,30.6-15.8,20.26,0,41.74,11.35,41.74,44.58v71.74h-33.64Z"/>
 <path fill={accentColor} d="M454.8,283.77c-4.86-10.54-13.57-15.8-25.13-15.8-15.4,0-25.94,12.77-25.94,28.57s11.15,28.58,26.75,28.58c11.35,0,19.25-5.27,24.31-15h34.25c-6.69,28.17-30.81,46.41-59.58,46.41-33.24,0-59.58-26.95-59.58-60.19s26.54-59.78,58.97-59.78c29.59,0,53.71,18.64,59.99,47.21h-34.05Z"/>
 <polygon fill={accentColor} points="288.88 483.82 288.88 364.38 255.24 364.38 255.24 514.34 288.88 514.34 300.14 514.34 300.14 483.82 288.88 483.82"/>
 <path fill={accentColor} d="M395.84,514.34v-12.16h-.41c-4.87,9.93-18.24,16.01-33.03,16.01-33.23,0-56.33-26.14-56.33-60.18s24.11-59.98,56.33-59.98c13.78,0,26.55,5.47,33.03,15.8h.41v-11.96h33.63v112.47h-33.63ZM395.84,458c0-15.8-12.77-28.57-28.58-28.57s-27.56,12.77-27.56,28.98,12.36,28.37,27.96,28.37,28.17-12.57,28.17-28.78Z"/>
 <circle fill={accentColor} cx="468.8" cy="495.13" r="23.06" opacity="0.5"/>
 <path fill={accentColor} d="M560.07,563.89H187.03V190.85h373.04v373.04ZM213.95,536.97h319.2V217.77H213.95v319.2Z"/>
 </svg>
 );
}
// ── STATUS SELECT ──────────────────────────────────────────────────────────────
function StatusSelect({ value, onChange, disabled }) {
 const opts = ["Active","Blocked","Complete","On Hold"];
 const colors = { Active:"#064E60", Blocked:"#771735", Complete:"#5a6e00", "On Hold":"#444" };
 const textColors = { Active:"#B7D9FF", Blocked:"#FFB7B8", Complete:"#D9E571", "On Hold":"#94a3b8" };
 return (
 <select className="ssel" value={value} onChange={e=>onChange(e.target.value)} disabled={disabled}
 style={{background:colors[value]||"#444",color:textColors[value]||"#fff",opacity:disabled?.6:1}}>
 {opts.map(o=><option key={o} value={o}>{o}</option>)}
 </select>
 );
}
// ── TASK ROW ──────────────────────────────────────────────────────────────────
function TaskRow({ task, onUpdate, cfg }) {
 const [open,setOpen]=useState(false);
 const [editNote,setEditNote]=useState(false);
 const [noteVal,setNoteVal]=useState(task.notes||"");
 const [buildSlot,setBuildSlot]=useState("");
 const [slotSent,setSlotSent]=useState(false);
 const [transitSent,setTransitSent]=useState(false);
 const p = cfg.brand.primaryColor;
 const h = cfg.brand.highlightColor;
 const info = cfg.brand.infoColor;
 const isBuildSlot = task.name.toLowerCase().includes("build slot confirmation");
 const isTransit = task.name.toLowerCase().includes("in transit");
 const isCommissionTrigger = task.name.toLowerCase().includes("deal complete") || task.name.toLowerCase().includes("commission payment logged");
 const done = task.status==="Complete";
 const blk = task.status==="Blocked";
 const typeColor = task.type==="auto"?h:task.type==="watch"?info:p;
 const typeLabel = task.type==="auto"?"Auto":task.type==="watch"?"Watch":"Action";
 const toggle = () => {
 if(task.type!=="action") return;
 onUpdate({...task, status: done?"Active":"Complete"});
 };
 const chkClass = task.type==="auto"?"chk auto-chk":task.type==="watch"?"chk watch-chk":done?"chk on":"chk";
 const chkIcon = task.type==="auto"?" ":task.type==="watch"?" ":done?"✓":"";
 return (
 <div className={`trow${done?" done":""}${blk?" blk":""}`}>
 <div className="trow-main" onClick={()=>setOpen(o=>!o)}>
 <div className="trow-left">
 <div className="type-bar" style={{background:typeColor}}/>
 <div className={chkClass} onClick={e=>{e.stopPropagation();toggle();}}>{chkIcon}</div>
 <div className="tinfo">
 <div className={`tname${done?" done":""}`}>{task.name}</div>
 <div className="tmeta">
 <span className="type-pill" style={{background:typeColor+"22",color:typeColor}}>{typeLabel}</span>
 {task.assignedTo&&<span className="due-tag">{task.assignedTo}</span>}
 {task.dueDate&&<span className="due-tag"> {task.dueDate}</span>}
 </div>
 {task.notes&&!editNote&&<div className="note-txt" onClick={e=>{e.stopPropagation();setEditNote(true);}}> {task.notes}</div>}
 {editNote&&(
 <div className="note-row" onClick={e=>e.stopPropagation()}>
 <input className="fi" value={noteVal} onChange={e=>setNoteVal(e.target.value)} placeholder="Add note..." onBlur={()=>{onUpdate({...task,notes:noteVal});setEditNote(false);}}/>
 </div>
 )}
 {!task.notes&&!editNote&&<div className="note-txt" onClick={e=>{e.stopPropagation();setEditNote(true);}}>+ add note</div>}
 </div>
 </div>
 <div className="trow-right" onClick={e=>e.stopPropagation()}>
 {task.type==="action"&&<input type="date" className="date-in" value={task.dueDate||""} onChange={e=>onUpdate({...task,dueDate:e.target.value})}/>}
 <StatusSelect value={task.status||"Active"} onChange={v=>onUpdate({...task,status:v})} disabled={task.type!=="action"}/>
 </div>
 </div>
 {open&&(
 <div className="detail-panel">
 <div className="dp-detail">{task.detail}</div>
 {task.type==="auto"&&<div className="dp-auto-note"> This step is automated by DRIVE.AI — no action required from you.</div>}
 {task.type==="watch"&&<div className="dp-watch-note"> DRIVE.AI monitors this automatically and notifies you when action is needed.</div>}
 {isCommissionTrigger&&<div className="dp-watch-note"> Marking this Complete automatically creates a commission row and updates your targets.</div>}
 {isBuildSlot&&task.type==="auto"&&(
 <div className="trigger-box">
 <div className="trigger-lbl">Enter Build Slot Date to Trigger</div>
 <div className="trigger-row">
 <input type="date" className="trigger-input" value={buildSlot} onChange={e=>setBuildSlot(e.target.value)}/>
 <button className="btn btn-primary btn-sm" disabled={!buildSlot||slotSent} onClick={()=>{setSlotSent(true);onUpdate({...task,status:"Complete",dueDate:buildSlot});}}>
 {slotSent?"✓ Sent":"Send Confirmation"}
 </button>
 </div>
 {slotSent&&<div className="trigger-sent">✓ Build slot confirmation sent to customer</div>}
 </div>
 )}
 {isTransit&&task.type==="action"&&(
 <div className="trigger-box">
 <div className="trigger-lbl">Mark Vehicle In Transit</div>
 <div className="trigger-row">
 <button className="btn btn-primary btn-sm" disabled={transitSent} onClick={()=>{setTransitSent(true);onUpdate({...task,status:"Complete"});}}>
 {transitSent?"✓ In Transit":"Confirm In Transit"}
 </button>
 </div>
 {transitSent&&<div className="trigger-sent">✓ In-transit notification sent to customer</div>}
 </div>
 )}
 </div>
 )}
 </div>
 );
}
// ── PHASE GROUP ───────────────────────────────────────────────────────────────
function PhaseGroup({ phaseName, tasks, onUpdate, cfg }) {
 const done = tasks.filter(t=>t.status==="Complete").length;
 const actLeft = tasks.filter(t=>t.type==="action"&&t.status!=="Complete").length;
 return (
 <div>
 <div className="phase-hdr">
 {phaseName}
 <span style={{marginLeft:"auto",fontSize:10,color:cfg.brand.infoColor+"77",fontFamily:"'DM Sans',sans-serif",fontWeight:600,letterSpacing:".03em"}}>
 {done}/{tasks.length} · {actLeft} action{actLeft!==1?"s":""} remaining
 </span>
 </div>
 {tasks.map(t=><TaskRow key={t.id} task={t} onUpdate={onUpdate} cfg={cfg}/>)}
 </div>
 );
}
// ── SETUP WIZARD ─────────────────────────────────────────────────────────────
function SetupWizard({ onComplete }) {
 const [step, setStep] = useState(0);
 const [cfg, setCfg] = useState({
 brand: { ...DEFAULT_CONFIG.brand },
 workflow: { phases: [...DEFAULT_CONFIG.workflow.phases], tasks: DEFAULT_CONFIG.workflow.tasks },
 commission: { columns: [...DEFAULT_CONFIG.commission.columns], funders: [...DEFAULT_CONFIG.commission.funders], statuses: [...DEFAULT_CONFIG.commission.statuses] },
 targets: { labels: [...DEFAULT_CONFIG.targets.labels], annual: [...DEFAULT_CONFIG.targets.annual] },
 access: { ...DEFAULT_CONFIG.access },
 });
 const set = (section, key, val) => setCfg(c=>({...c,[section]:{...c[section],[key]:val}}));
 const total = 5;
 const pct = Math.round((step/total)*100);
 const steps = [
 {
 title: "Welcome to DRIVE.AI",
 sub: "Let's set up your platform. This takes about two minutes and you can change everything later from the admin panel.",
 content: (
 <div style={{textAlign:"center",padding:"20px 0"}}>
 <div style={{fontSize:64,marginBottom:16}}> </div>
 <p style={{fontSize:13,color:"rgba(183,217,255,.5)",lineHeight:1.6}}>Your commercial vehicle sales platform — fully configured for your organisation, your workflow and your brand.</p>
 </div>
 )
 },
 {
 title: "Your Brand",
 sub: "Set your organisation name and brand colours. These will appear throughout the app.",
 content: (
 <div>
 <div className="fg">
 <label className="fl">Organisation Name</label>
 <input className="fi" value={cfg.brand.name} onChange={e=>set("brand","name",e.target.value)} placeholder="e.g. MAN Truck & Bus"/>
 </div>
 <div className="fg">
 <label className="fl">Name Accent (shown in brand colour)</label>
 <input className="fi" value={cfg.brand.nameAccent} onChange={e=>set("brand","nameAccent",e.target.value)} placeholder="e.g. .AI or Sales"/>
 </div>
 <div className="fg">
 <label className="fl">Tagline</label>
 <input className="fi" value={cfg.brand.tagline} onChange={e=>set("brand","tagline",e.target.value)} placeholder="e.g. Commercial Vehicle Sales Platform"/>
 </div>
 <div className="admin-section-title">Brand Colours</div>
 <div className="color-row">
 {[
 ["primaryColor","Primary (buttons, accents)"],
 ["secondaryColor","Secondary (structure, borders)"],
 ["accentColor","Accent (nav, highlights)"],
 ["highlightColor","Highlight (positive values)"],
 ["infoColor","Info (Sky blue tone)"],
 ["navBg","Navigation Background"],
 ].map(([key,label])=>(
 <div className="color-fg" key={key}>
 <label className="fl">{label}</label>
 <div className="color-input-row">
 <input type="color" className="color-swatch" value={cfg.brand[key]} onChange={e=>set("brand",key,e.target.value)}/>
 <input className="fi" value={cfg.brand[key]} onChange={e=>set("brand",key,e.target.value)} style={{fontFamily:"monospace",fontSize:12}}/>
 </div>
 </div>
 ))}
 </div>
 </div>
 )
 },
 {
 title: "Sales Workflow",
 sub: "Customise the phases of your vehicle sales workflow. You can rename each phase to match your process.",
 content: (
 <div>
 <div className="admin-section-title">Workflow Phases</div>
 <div className="phase-list">
 {cfg.workflow.phases.map((phase,i)=>(
 <div className="phase-item" key={i}>
 <span className="phase-num">{i+1}</span>
 <input className="fi" value={phase} onChange={e=>{
 const phases=[...cfg.workflow.phases];
 const old=phases[i];
 phases[i]=e.target.value;
 const tasks=cfg.workflow.tasks.map(t=>t.phase===old?{...t,phase:e.target.value}:t);
 setCfg(c=>({...c,workflow:{...c.workflow,phases,tasks}}));
 }}/>
 </div>
 ))}
 </div>
 <p style={{fontSize:11,color:"rgba(183,217,255,.35)",marginTop:12,lineHeight:1.5}}>Tasks are assigned to each phase automatically. You can edit individual tasks from the admin panel after setup.</p>
 </div>
 )
 },
 {
 title: "Commission Tracking",
 sub: "Set up your commission columns to match how you earn. You can add or remove columns at any time.",
 content: (
 <div>
 <div className="admin-section-title">Commission Columns</div>
 <div className="comm-col-list">
 {cfg.commission.columns.map((col,i)=>(
 <div className="comm-col-chip" key={i}>
 <input value={col} onChange={e=>{
 const cols=[...cfg.commission.columns];
 cols[i]=e.target.value;
 set("commission","columns",cols);
 }}/>
 {cfg.commission.columns.length>1&&(
 <button style={{background:"none",border:"none",color:"#FFB7B8",cursor:"pointer",fontSize:12,padding:0}} onClick={()=>{
 const cols=cfg.commission.columns.filter((_,j)=>j!==i);
 set("commission","columns",cols);
 }}>✕</button>
 )}
 </div>
 ))}
 <button className="add-row-btn" onClick={()=>set("commission","columns",[...cfg.commission.columns,"New Column"])}>+ Add Column</button>
 </div>
 <div className="admin-section-title" style={{marginTop:20}}>Target Labels</div>
 <div className="fr">
 {cfg.targets.labels.map((label,i)=>(
 <div className="fg" key={i}>
 <label className="fl">Target {i+1}</label>
 <input className="fi" value={label} onChange={e=>{
 const labels=[...cfg.targets.labels];
 labels[i]=e.target.value;
 set("targets","labels",labels);
 }}/>
 </div>
 ))}
 </div>
 </div>
 )
 },
 {
 title: "Access & Security",
 sub: "Set your app password and admin password. Keep these safe — you'll need them to log in.",
 content: (
 <div>
 <div className="fg">
 <label className="fl">App Password (for daily use)</label>
 <input className="fi" type="password" value={cfg.access.appPassword} onChange={e=>set("access","appPassword",e.target.value)}/>
 </div>
 <div className="fg">
 <label className="fl">Admin Password (for settings changes)</label>
 <input className="fi" type="password" value={cfg.access.adminPassword} onChange={e=>set("access","adminPassword",e.target.value)}/>
 </div>
 <div style={{fontSize:11,color:"rgba(183,217,255,.35)",marginTop:8,lineHeight:1.6,padding:"12px 16px",background:"rgba(6,78,96,.15)",borderRadius:10,border:"1px solid rgba(6,78,96,.4)"}}>
 <strong style={{color:"rgba(183,217,255,.6)"}}>Important:</strong> Write these down before continuing. The app password is what you and your team use daily. The admin password unlocks settings.
 </div>
 </div>
 )
 },
 ];
 const current = steps[step];
 return (
 <div className="wizard-overlay">
 <div className="wizard">
 <div className="wizard-hdr">
 <div className="wizard-step">Setup — Step {step+1} of {total}</div>
 <div className="wizard-title">{current.title}</div>
 <div className="wizard-sub">{current.sub}</div>
 </div>
 <div className="progress-bar" style={{margin:"16px 32px 0"}}>
 <div className="progress-fill" style={{width:`${pct}%`}}/>
 </div>
 <div className="wizard-body">{current.content}</div>
 <div className="wizard-foot">
 {step>0&&<button className="btn btn-secondary" onClick={()=>setStep(s=>s-1)}>← Back</button>}
 {step<total-1
 ? <button className="btn btn-primary" onClick={()=>setStep(s=>s+1)}>Continue →</button>
 : <button className="btn btn-primary" onClick={()=>onComplete(cfg)}>Launch DRIVE.AI </button>
 }
 </div>
 </div>
 </div>
 );
}
// ── PROSPECTS SCREEN ──────────────────────────────────────────────────────────
function ProspectsScreen({ cfg }) {
 const [prospects, setProspects] = useState(() => loadData("prospects_v2", []));
 const [showAdd, setShowAdd] = useState(false);
 const [activeTab, setActiveTab] = useState("list");
 const [searchQuery, setSearchQuery] = useState("");
 const [searchType, setSearchType] = useState("company");
 const [searchResults, setSearchResults] = useState([]);
 const [searching, setSearching] = useState(false);
 const [searchError, setSearchError] = useState("");
 const [monitorPostcode, setMonitorPostcode] = useState("");
 const [newDiscoveries, setNewDiscoveries] = useState(() => loadData("discoveries_v1", []));
 const [olicenceText, setOlicenceText] = useState("");
 const [olicenceParsed, setOlicenceParsed] = useState([]);
 const [form, setForm] = useState({name:"",company:"",email:"",phone:"",vehicle:"",notes:"",stage:"New Lead"});
 const stages = ["New Lead","Contacted","Qualified","Proposal Sent","Negotiating","Won","Lost"];
 const stageColors = {"New Lead":"#064E60","Contacted":"#065880","Qualified":"#0a7a9a","Proposal Sent":"#FC4D16","Negotiating":"#e04410","Won":"#5a6e00","Lost":"#771735"};
 const stageText = {"New Lead":"#B7D9FF","Contacted":"#B7D9FF","Qualified":"#B7D9FF","Proposal Sent":"#fff","Negotiating":"#fff","Won":"#D9E571","Lost":"#FFB7B8"};
 const p = cfg.brand.primaryColor;
 useEffect(()=>saveData("prospects_v2",prospects),[prospects]);
 useEffect(()=>saveData("discoveries_v1",newDiscoveries),[newDiscoveries]);
 const searchCompaniesHouse = async () => {
 if(!searchQuery.trim()) return;
 setSearching(true); setSearchError(""); setSearchResults([]);
 try {
 const apiType = searchType==="number" ? "number" : searchType==="postcode" ? "location" : "";
 const url = `/api/companies-house?q=${encodeURIComponent(searchQuery.trim())}${apiType?`&type=${apiType}`:""}`;
 const res = await fetch(url);
 const data = await res.json();
 if(!res.ok) throw new Error(data.error || "Search failed.");
 setSearchResults((data.results||[]).map(r=>({
 ...r,
 fleetLikely: (r.sic||"").split(",").some(code=>FLEET_LIKELY_SIC.includes(code.trim())),
 })));
 } catch(e) {
 setSearchError(e.message || "Search failed. Please check the server is configured with a Companies House API key.");
 }
 setSearching(false);
 };
 const runSearch = () => searchCompaniesHouse();
 const addFromResult = (result) => {
 setProspects(ps=>[...ps,{
 id:uid(),
 name:"",
 company:result.name,
 companyNumber:result.number,
 address:result.address,
 email:"",phone:"",vehicle:"",
 notes:`${result.type||""} ${result.sic?"· SIC: "+result.sic:""} ${result.incorporated?"· Inc: "+result.incorporated:""} ${result.fleetLikely?"· Likely fleet operator":""}`.trim(),
 stage:"New Lead",
 source:"search",
 createdAt:new Date().toISOString()
 }]);
 setActiveTab("list");
 };
 const dismissDiscovery = (id) => setNewDiscoveries(d=>d.filter(x=>x.id!==id));
 const addDiscoveryAsProspect = (disc) => {
 setProspects(ps=>[...ps,{id:uid(),name:"",company:disc.name,email:"",phone:"",vehicle:"",notes:disc.detail,stage:"New Lead",source:"auto",createdAt:new Date().toISOString()}]);
 dismissDiscovery(disc.id);
 };
 // Parse pasted Traffic Commissioner "Applications and Decisions" text into operator entries
 const parseOLicenceText = () => {
 if(!olicenceText.trim()) { setOlicenceParsed([]); return; }
 const lines = olicenceText.split("\n").map(l=>l.trim()).filter(Boolean);
 const postcodeRe = /([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})/i;
 const parsed = lines
 .filter(l=>l.length>8)
 .map(l=>{
 const pcMatch = l.match(postcodeRe);
 return {
 id: uid(),
 raw: l,
 postcode: pcMatch ? pcMatch[1].toUpperCase().replace(/\s+/,' ') : "",
 };
 })
 .filter(entry=>{
 const watched = cfg.prospecting?.targetPostcodes||[];
 if(watched.length===0) return true;
 return watched.some(pc=>entry.postcode.toUpperCase().startsWith(pc.toUpperCase()));
 });
 setOlicenceParsed(parsed);
 };
 const addOLicenceAsDiscovery = (entry) => {
 setNewDiscoveries(d=>[...d,{
 id:uid(),
 type:"O-Licence",
 name: entry.raw.split(/\s{2,}/)[0] || entry.raw.slice(0,60),
 detail: entry.raw,
 date: new Date().toLocaleDateString("en-GB"),
 }]);
 setOlicenceParsed(o=>o.filter(x=>x.id!==entry.id));
 };
 const add = () => {
 setProspects(ps=>[...ps,{id:uid(),...form,createdAt:new Date().toISOString()}]);
 setForm({name:"",company:"",email:"",phone:"",vehicle:"",notes:"",stage:"New Lead"});
 setShowAdd(false);
 };
 const innerTabs = [
 {id:"list",label:`My Prospects (${prospects.length})`},
 {id:"search",label:" Search"},
 {id:"olicence",label:" O-Licence Watch"},
 {id:"discover",label:` Discoveries ${newDiscoveries.length>0?`(${newDiscoveries.length})`:""}` },
 ];
 return (
 <div>
 <div className="sh">
 <div><div className="sh-title"> Prospects</div><div className="sh-sub">Prospect pipeline & discovery</div></div>
 <button className="btn btn-primary" onClick={()=>setShowAdd(true)}>+ Add Prospect</button>
 </div>
 {/* Inner tabs */}
 <div style={{display:"flex",gap:4,marginBottom:20,flexWrap:"wrap"}}>
 {innerTabs.map(t=>(
 <button key={t.id} onClick={()=>setActiveTab(t.id)}
 style={{fontSize:11,fontWeight:700,padding:"7px 16px",borderRadius:100,border:"none",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",letterSpacing:".03em",
 background:activeTab===t.id?p:"rgba(6,78,96,.25)",color:activeTab===t.id?"#fff":"rgba(183,217,255,.5)",transition:"all .15s"}}>
 {t.label}
 </button>
 ))}
 </div>
 {/* PROSPECTS LIST */}
 {activeTab==="list"&&(
 <>
 <div className="stats">
 {stages.slice(0,4).map(s=>(
 <div className="stat" key={s}>
 <div className="stat-lbl">{s}</div>
 <div className="stat-val blu">{prospects.filter(pr=>pr.stage===s).length}</div>
 </div>
 ))}
 </div>
 {prospects.length===0?(
 <div className="empty"><div className="empty-icon"> </div><div className="empty-ttl">No prospects yet</div><p style={{fontSize:12,color:"rgba(183,217,255,.2)"}}>Use Search to find companies or add manually</p></div>
 ):(
 <div style={{display:"flex",flexDirection:"column",gap:8}}>
 {prospects.map(pr=>(
 <div key={pr.id} style={{background:"rgba(6,78,96,.15)",border:"1px solid rgba(6,78,96,.4)",borderRadius:12,padding:"16px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
 <div style={{flex:1,minWidth:0}}>
 <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
 <div style={{fontFamily:"'Special Gothic Expanded One',sans-serif",fontSize:13,color:"#FFF5E9"}}>{pr.company||pr.name}</div>
 {pr.source==="auto"&&<span style={{fontSize:9,padding:"1px 7px",borderRadius:100,background:"rgba(217,229,113,.15)",color:"#D9E571",fontWeight:700,textTransform:"uppercase"}}>Auto</span>}
 {pr.source==="search"&&<span style={{fontSize:9,padding:"1px 7px",borderRadius:100,background:"rgba(183,217,255,.1)",color:"#B7D9FF",fontWeight:700,textTransform:"uppercase"}}>CH</span>}
 </div>
 {pr.name&&pr.company&&<div style={{fontSize:11,color:"rgba(183,217,255,.45)"}}>{pr.name}</div>}
 {pr.address&&<div style={{fontSize:11,color:"rgba(183,217,255,.3)",marginTop:2}}> {pr.address}</div>}
 {pr.vehicle&&<div style={{fontSize:11,color:"#B7D9FF",marginTop:2}}> {pr.vehicle}</div>}
 {pr.notes&&<div style={{fontSize:11,color:"rgba(183,217,255,.25)",marginTop:3,fontStyle:"italic"}}>{pr.notes}</div>}
 </div>
 <div style={{display:"flex",alignItems:"center",gap:10}}>
 <select value={pr.stage} onChange={e=>setProspects(ps=>ps.map(x=>x.id===pr.id?{...x,stage:e.target.value}:x))}
 style={{background:stageColors[pr.stage]||"#333",color:stageText[pr.stage]||"#fff",border:"none",borderRadius:100,padding:"5px 12px",fontSize:10,fontWeight:700,fontFamily:"'DM Sans',sans-serif",cursor:"pointer",textTransform:"uppercase",letterSpacing:".04em"}}>
 {stages.map(s=><option key={s} value={s}>{s}</option>)}
 </select>
 <button className="btn btn-ghost" style={{color:"#FFB7B8",padding:"4px 8px"}} onClick={()=>setProspects(ps=>ps.filter(x=>x.id!==pr.id))}>✕</button>
 </div>
 </div>
 ))}
 </div>
 )}
 </>
 )}
 {/* SEARCH */}
 {activeTab==="search"&&(
 <div>
 <div style={{background:"rgba(6,78,96,.15)",border:"1px solid rgba(6,78,96,.4)",borderRadius:14,padding:20,marginBottom:20}}>
 <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
 {[["company"," Company Name"],["postcode"," Postcode"],["number","# Company No."]].map(([type,label])=>(
 <button key={type} onClick={()=>setSearchType(type)}
 style={{fontSize:11,fontWeight:700,padding:"6px 14px",borderRadius:100,border:"none",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",
 background:searchType===type?p:"rgba(6,78,96,.3)",color:searchType===type?"#fff":"rgba(183,217,255,.5)"}}>
 {label}
 </button>
 ))}
 </div>
 <div style={{display:"flex",gap:10}}>
 <input className="fi" style={{flex:1}} value={searchQuery}
 onChange={e=>setSearchQuery(e.target.value)}
 onKeyDown={e=>e.key==="Enter"&&runSearch()}
 placeholder={searchType==="company"?"Search company name...":searchType==="postcode"?"Enter postcode or area...":"Enter company number..."}/>
 <button className="btn btn-primary" onClick={runSearch} disabled={searching}>{searching?"Searching...":"Search"}</button>
 </div>
 {searchError&&<div style={{fontSize:12,color:"#FFB7B8",marginTop:10,padding:"8px 12px",background:"rgba(119,23,53,.15)",borderRadius:8}}>{searchError}</div>}
 </div>
 {searchResults.length>0&&(
 <div style={{display:"flex",flexDirection:"column",gap:8}}>
 <div style={{fontSize:10,color:"rgba(183,217,255,.4)",textTransform:"uppercase",letterSpacing:".1em",marginBottom:4}}>{searchResults.length} results</div>
 {searchResults.map(r=>(
 <div key={r.id} style={{background:"rgba(6,78,96,.12)",border:"1px solid rgba(6,78,96,.4)",borderRadius:12,padding:"14px 16px",display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
 <div style={{flex:1}}>
 <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
 <div style={{fontFamily:"'Special Gothic Expanded One',sans-serif",fontSize:13,color:"#FFF5E9"}}>{r.name}</div>
 {r.fleetLikely&&<span style={{fontSize:9,padding:"1px 7px",borderRadius:100,background:"rgba(217,229,113,.15)",color:"#D9E571",fontWeight:700,textTransform:"uppercase"}}>Likely Fleet</span>}
 </div>
 <div style={{fontSize:11,color:"rgba(183,217,255,.4)"}}>
 {r.number&&<span style={{marginRight:12}}>#{r.number}</span>}
 {r.status&&<span style={{marginRight:12,textTransform:"capitalize"}}>{r.status}</span>}
 {r.type&&<span style={{marginRight:12}}>{r.type}</span>}
 </div>
 {r.address&&<div style={{fontSize:11,color:"rgba(183,217,255,.3)",marginTop:4}}> {r.address}</div>}
 {r.sic&&<div style={{fontSize:10,color:"rgba(183,217,255,.25)",marginTop:3}}>SIC: {r.sic}</div>}
 {r.incorporated&&<div style={{fontSize:10,color:"rgba(183,217,255,.25)",marginTop:2}}>Incorporated: {r.incorporated}</div>}
 </div>
 <button className="btn btn-primary btn-sm" onClick={()=>addFromResult(r)}>+ Add Prospect</button>
 </div>
 ))}
 </div>
 )}
 {searchResults.length===0&&!searching&&!searchError&&(
 <div className="empty" style={{padding:"40px 24px"}}>
 <div className="empty-icon"> </div>
 <div className="empty-ttl">Search Companies House</div>
 <p style={{fontSize:12,color:"rgba(183,217,255,.2)",marginTop:8}}>Searches run securely via the server using your configured API key</p>
 </div>
 )}
 </div>
 )}
 {/* O-LICENCE WATCH */}
 {activeTab==="olicence"&&(
 <div>
 <div style={{background:"rgba(6,78,96,.15)",border:"1px solid rgba(6,78,96,.4)",borderRadius:14,padding:20,marginBottom:20}}>
 <div style={{fontFamily:"'Special Gothic Expanded One',sans-serif",fontSize:13,color:"#FFF5E9",marginBottom:8,letterSpacing:".04em"}}>O-Licence Applications & Decisions</div>
 <div style={{fontSize:12,color:"rgba(183,217,255,.45)",marginBottom:14,lineHeight:1.5}}>
 There is no public API for Traffic Commissioner O-Licence data. Paste the text from the weekly "Applications and Decisions" PDF for your traffic area below — published on gov.uk — and matching entries (filtered by your target postcodes in Settings → Integrations) will be parsed out so you can send them to Discoveries.
 </div>
 <textarea className="fta" style={{minHeight:140}} value={olicenceText} onChange={e=>setOlicenceText(e.target.value)} placeholder="Paste Applications and Decisions text here..."/>
 <div style={{marginTop:10}}>
 <button className="btn btn-primary btn-sm" onClick={parseOLicenceText}>Parse Text</button>
 </div>
 </div>
 {olicenceParsed.length>0&&(
 <div style={{display:"flex",flexDirection:"column",gap:8}}>
 <div style={{fontSize:10,color:"rgba(183,217,255,.4)",textTransform:"uppercase",letterSpacing:".1em",marginBottom:4}}>{olicenceParsed.length} matching entries</div>
 {olicenceParsed.map(entry=>(
 <div key={entry.id} style={{background:"rgba(6,78,96,.12)",border:"1px solid rgba(6,78,96,.4)",borderRadius:12,padding:"14px 16px",display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
 <div style={{flex:1,fontSize:12,color:"rgba(183,217,255,.6)",lineHeight:1.5}}>{entry.raw}</div>
 <button className="btn btn-primary btn-sm" onClick={()=>addOLicenceAsDiscovery(entry)}>+ Send to Discoveries</button>
 </div>
 ))}
 </div>
 )}
 </div>
 )}
 {/* AUTO-DISCOVERIES */}
 {activeTab==="discover"&&(
 <div>
 <div style={{background:"rgba(6,78,96,.15)",border:"1px solid rgba(6,78,96,.4)",borderRadius:14,padding:20,marginBottom:20}}>
 <div style={{fontFamily:"'Special Gothic Expanded One',sans-serif",fontSize:13,color:"#FFF5E9",marginBottom:8,letterSpacing:".04em"}}>Target Postcodes</div>
 <div style={{fontSize:12,color:"rgba(183,217,255,.45)",marginBottom:14,lineHeight:1.5}}>Manage your watched postcodes in Settings → Integrations. Items sent here from Search or O-Licence Watch appear below.</div>
 <div style={{display:"flex",gap:10,marginBottom:12}}>
 <input className="fi" style={{flex:1}} value={monitorPostcode} onChange={e=>setMonitorPostcode(e.target.value.toUpperCase())} placeholder="View only — edit in Settings" disabled/>
 </div>
 {(cfg.prospecting?.targetPostcodes||[]).length>0&&(
 <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
 {(cfg.prospecting?.targetPostcodes||[]).map(pc=>(
 <span key={pc} style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:100,background:"rgba(6,78,96,.4)",color:"#B7D9FF"}}>{pc}</span>
 ))}
 </div>
 )}
 </div>
 {newDiscoveries.length===0?(
 <div className="empty" style={{padding:"40px 24px"}}>
 <div className="empty-icon"> </div>
 <div className="empty-ttl">No new discoveries yet</div>
 <p style={{fontSize:12,color:"rgba(183,217,255,.2)",marginTop:8}}>Send items here from Search or O-Licence Watch</p>
 </div>
 ):(
 <div style={{display:"flex",flexDirection:"column",gap:8}}>
 {newDiscoveries.map(d=>(
 <div key={d.id} style={{background:"rgba(217,229,113,.05)",border:"1px solid rgba(217,229,113,.2)",borderRadius:12,padding:"14px 16px",display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
 <div style={{flex:1}}>
 <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
 <span style={{fontSize:9,padding:"2px 8px",borderRadius:100,background:"rgba(217,229,113,.15)",color:"#D9E571",fontWeight:700,textTransform:"uppercase",letterSpacing:".04em"}}>{d.type}</span>
 <span style={{fontSize:10,color:"rgba(183,217,255,.35)"}}>{d.date}</span>
 </div>
 <div style={{fontFamily:"'Special Gothic Expanded One',sans-serif",fontSize:13,color:"#FFF5E9"}}>{d.name}</div>
 <div style={{fontSize:11,color:"rgba(183,217,255,.4)",marginTop:3}}>{d.detail}</div>
 </div>
 <div style={{display:"flex",gap:8}}>
 <button className="btn btn-primary btn-sm" onClick={()=>addDiscoveryAsProspect(d)}>+ Add</button>
 <button className="btn btn-ghost btn-sm" style={{color:"rgba(183,217,255,.35)"}} onClick={()=>dismissDiscovery(d.id)}>Dismiss</button>
 </div>
 </div>
 ))}
 </div>
 )}
 </div>
 )}
 {showAdd&&(
 <div className="overlay">
 <div className="modal">
 <div className="mh"><span className="mt">Add Prospect</span><button className="btn btn-ghost" onClick={()=>setShowAdd(false)}>✕</button></div>
 <div className="mb">
 <div className="fr"><div className="fg"><label className="fl">Name</label><input className="fi" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}/></div><div className="fg"><label className="fl">Company</label><input className="fi" value={form.company} onChange={e=>setForm(f=>({...f,company:e.target.value}))}/></div></div>
 <div className="fr"><div className="fg"><label className="fl">Email</label><input className="fi" type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))}/></div><div className="fg"><label className="fl">Phone</label><input className="fi" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))}/></div></div>
 <div className="fg"><label className="fl">Vehicle Interest</label><input className="fi" value={form.vehicle} onChange={e=>setForm(f=>({...f,vehicle:e.target.value}))}/></div>
 <div className="fg"><label className="fl">Notes</label><textarea className="fta" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></div>
 <div className="ma"><button className="btn btn-secondary" onClick={()=>setShowAdd(false)}>Cancel</button><button className="btn btn-primary" onClick={add}>Add Prospect</button></div>
 </div>
 </div>
 </div>
 )}
 </div>
 );
}
// ── RELATIONSHIPS SCREEN ──────────────────────────────────────────────────────
function RelationshipsScreen({ cfg }) {
 const [contacts, setContacts] = useState(() => loadData("relationships_v2", []));
 const [showAdd, setShowAdd] = useState(false);
 const [selected, setSelected] = useState(null);
 const [emailStatus, setEmailStatus] = useState({});
 const [form, setForm] = useState({name:"",company:"",email:"",phone:"",handoverDate:"",vehicleSpec:"",notes:"",priority:"Medium"});
 const priorities = ["High","Medium","Low"];
 const priorityColors = { High:"rgba(252,77,22,.2)", Medium:"rgba(6,78,96,.3)", Low:"rgba(6,78,96,.15)" };
 const priorityText = { High:"#FC4D16", Medium:"#B7D9FF", Low:"rgba(183,217,255,.4)" };
 useEffect(()=>saveData("relationships_v2",contacts),[contacts]);
 const getDaysSince = (dateStr) => {
 if(!dateStr) return null;
 const diff = Date.now() - new Date(dateStr).getTime();
 return Math.floor(diff/(1000*60*60*24));
 };
 const getTouchpointStatus = (contact, tp) => {
 const days = getDaysSince(contact.handoverDate);
 if(days === null) return "no-date";
 const log = (contact.touchpointLog || {})[tp.key];
 if(log?.sent) return "sent";
 if(days >= tp.days) return "due";
 if(days >= tp.days - 7) return "upcoming";
 return "future";
 };
 const getAutoSend = (contact, tpKey) => (contact.autoSend||{})[tpKey] ?? false;
 const setAutoSend = (contactId, tpKey, val) => {
 setContacts(cs=>cs.map(c=>c.id===contactId?{...c,autoSend:{...(c.autoSend||{}),[tpKey]:val}}:c));
 };
 const sendTouchpoint = async (contact, tp) => {
 const template = cfg.emailTemplates?.[tp.key];
 if(!template) return;
 const vars = {
 customerName: contact.name || contact.company,
 vehicleSpec: contact.vehicleSpec || "your vehicle",
 senderName: cfg.email?.fromName || "Your Sales Executive",
 };
 const subject = fillTemplate(template.subject, vars);
 const body = fillTemplate(template.body, vars);
 setEmailStatus(s=>({...s,[`${contact.id}_${tp.key}`]:"sending"}));
 const result = await sendEmail(cfg, contact.email, subject, body);
 const newContacts = contacts.map(c => c.id===contact.id ? {
 ...c,
 touchpointLog: {
 ...(c.touchpointLog||{}),
 [tp.key]: { sent: true, date: new Date().toISOString(), method: result.sent?"email":"clipboard" }
 }
 } : c);
 setContacts(newContacts);
 setEmailStatus(s=>({...s,[`${contact.id}_${tp.key}`]:result.copied?"copied":"sent"}));
 setTimeout(()=>setEmailStatus(s=>({...s,[`${contact.id}_${tp.key}`]:undefined})),3000);
 };
 // Auto-send pass: runs on mount and whenever contacts/cfg change.
 // Any touchpoint marked autoSend that has become due and hasn't been sent yet fires automatically.
 useEffect(()=>{
 contacts.forEach(contact=>{
 TOUCHPOINTS.forEach(tp=>{
 const status = getTouchpointStatus(contact, tp);
 const log = (contact.touchpointLog||{})[tp.key];
 const auto = getAutoSend(contact, tp.key);
 const key = `${contact.id}_${tp.key}`;
 if(auto && status==="due" && !log?.sent && contact.email && emailStatus[key]===undefined){
 sendTouchpoint(contact, tp);
 }
 });
 });
 // eslint-disable-next-line react-hooks/exhaustive-deps
 },[contacts.length]);
 const add = () => {
 setContacts(c=>[...c,{id:uid(),...form,touchpointLog:{},autoSend:{}}]);
 setForm({name:"",company:"",email:"",phone:"",handoverDate:"",vehicleSpec:"",notes:"",priority:"Medium"});
 setShowAdd(false);
 };
 const sorted = [...contacts].sort((a,b)=>priorities.indexOf(a.priority)-priorities.indexOf(b.priority));
 const dueCount = contacts.reduce((n,c)=>n+TOUCHPOINTS.filter(tp=>getTouchpointStatus(c,tp)==="due"&&!getAutoSend(c,tp.key)).length,0);
 if(selected) {
 const contact = contacts.find(c=>c.id===selected);
 if(!contact){setSelected(null);return null;}
 const days = getDaysSince(contact.handoverDate);
 return (
 <div>
 <button className="back-btn" onClick={()=>setSelected(null)}>← Back to Relationships</button>
 <div className="dhdr">
 <div className="dh-row">
 <div>
 <div className="dh-name">{contact.name||contact.company}</div>
 <div className="dh-co">{contact.company}</div>
 {contact.vehicleSpec&&<div className="dh-veh">{contact.vehicleSpec}</div>}
 <div className="dh-meta">
 {contact.email&&<div className="mi"><strong>Email:</strong> {contact.email}</div>}
 {contact.phone&&<div className="mi"><strong>Phone:</strong> {contact.phone}</div>}
 {contact.handoverDate&&<div className="mi"><strong>Handover:</strong> {contact.handoverDate} {days!==null&&`(${days} days ago)`}</div>}
 </div>
 </div>
 <div className="dh-right">
 <span style={{fontSize:9,fontWeight:700,padding:"3px 10px",borderRadius:100,background:priorityText[contact.priority]+"22",color:priorityText[contact.priority],textTransform:"uppercase",letterSpacing:".04em"}}>{contact.priority} Priority</span>
 <button className="btn btn-danger btn-sm" onClick={()=>{setContacts(cs=>cs.filter(c=>c.id!==selected));setSelected(null);}}>Remove</button>
 </div>
 </div>
 </div>
 <div className="sh-title" style={{fontSize:16,marginBottom:16}}>Contact Sequence</div>
 <div style={{display:"flex",flexDirection:"column",gap:10}}>
 {TOUCHPOINTS.map(tp=>{
 const status = getTouchpointStatus(contact, tp);
 const log = (contact.touchpointLog||{})[tp.key];
 const emailKey = `${contact.id}_${tp.key}`;
 const sending = emailStatus[emailKey]==="sending";
 const justSent = emailStatus[emailKey]==="sent"||emailStatus[emailKey]==="copied";
 const template = cfg.emailTemplates?.[tp.key];
 const auto = getAutoSend(contact, tp.key);
 const vars = { customerName:contact.name||contact.company, vehicleSpec:contact.vehicleSpec||"your vehicle", senderName:cfg.email?.fromName||"Your Sales Executive" };
 return (
 <div key={tp.key} style={{
 background: status==="sent"?"rgba(6,78,96,.1)":status==="due"?"rgba(252,77,22,.08)":status==="upcoming"?"rgba(217,229,113,.06)":"rgba(6,78,96,.06)",
 border: `1px solid ${status==="sent"?"rgba(6,78,96,.3)":status==="due"?"rgba(252,77,22,.3)":status==="upcoming"?"rgba(217,229,113,.2)":"rgba(6,78,96,.2)"}`,
 borderRadius:12,padding:"14px 16px",
 }}>
 <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
 <div style={{display:"flex",alignItems:"center",gap:12}}>
 <div style={{
 width:36,height:36,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",
 background: status==="sent"?"rgba(6,78,96,.4)":status==="due"?"rgba(252,77,22,.2)":status==="upcoming"?"rgba(217,229,113,.15)":"rgba(6,78,96,.15)",
 fontSize:14,flexShrink:0
 }}>
 {status==="sent"?"✓":status==="due"?" ":status==="upcoming"?" ":" "}
 </div>
 <div>
 <div style={{fontFamily:"'Special Gothic Expanded One',sans-serif",fontSize:12,color:status==="sent"?"rgba(183,217,255,.5)":"#FFF5E9",letterSpacing:".04em"}}>{tp.label} Post Handover</div>
 <div style={{fontSize:10,color:"rgba(183,217,255,.35)",marginTop:3}}>
 {status==="sent"&&log?`Sent ${new Date(log.date).toLocaleDateString("en-GB")} via ${log.method}`:
 status==="due"?`Due — ${days!==null?days-tp.days+" days overdue":"set handover date"}`:
 status==="upcoming"?"Due soon":
 status==="no-date"?"Set a handover date to activate":
 `In ${contact.handoverDate?tp.days-days+" days":""}`}
 </div>
 </div>
 </div>
 <div style={{display:"flex",gap:12,alignItems:"center"}}>
 {status!=="sent"&&(
 <label style={{display:"flex",alignItems:"center",gap:6,fontSize:10,color:"rgba(183,217,255,.5)",fontWeight:700,textTransform:"uppercase",letterSpacing:".04em",cursor:"pointer"}}>
 <input type="checkbox" checked={auto} onChange={e=>setAutoSend(contact.id,tp.key,e.target.checked)}/>
 Auto-send
 </label>
 )}
 {template&&(status==="due"||status==="upcoming")&&!log?.sent&&(
 <button className="btn btn-primary btn-sm" disabled={sending||!contact.email}
 onClick={()=>sendTouchpoint(contact,tp)}
 title={!contact.email?"Add email address to send":auto?"Send now instead of waiting for auto-send":"Send touchpoint email"}>
 {sending?"Sending...":justSent?"✓ Sent":"Send Now"}
 </button>
 )}
 {status==="sent"&&(
 <button className="btn btn-ghost btn-sm" onClick={()=>{
 const newContacts=contacts.map(c=>c.id===contact.id?{...c,touchpointLog:{...(c.touchpointLog||{}),[tp.key]:undefined}}:c);
 setContacts(newContacts);
 }}>Undo</button>
 )}
 </div>
 </div>
 {auto&&status!=="sent"&&(
 <div style={{marginTop:10,fontSize:10,color:"#D9E571",fontWeight:600}}> Will send automatically when due — switch off to require your review first</div>
 )}
 {template&&status!=="sent"&&(
 <div style={{marginTop:12,padding:"10px 12px",background:"rgba(0,0,0,.15)",borderRadius:8,fontSize:11,color:"rgba(183,217,255,.35)"}}>
 <div style={{fontWeight:700,color:"rgba(183,217,255,.5)",marginBottom:4}}>{fillTemplate(template.subject,vars)}</div>
 <div style={{lineHeight:1.6,whiteSpace:"pre-line"}}>{fillTemplate(template.body,vars).substring(0,180)}...</div>
 </div>
 )}
 </div>
 );
 })}
 </div>
 </div>
 );
 }
 return (
 <div>
 <div className="sh">
 <div>
 <div className="sh-title"> Relationships</div>
 <div className="sh-sub">{contacts.length} contacts · {dueCount} touchpoint{dueCount!==1?"s":""} need your review</div>
 </div>
 <button className="btn btn-primary" onClick={()=>setShowAdd(true)}>+ Add Contact</button>
 </div>
 {dueCount>0&&(
 <div style={{background:"rgba(252,77,22,.08)",border:"1px solid rgba(252,77,22,.25)",borderRadius:12,padding:"12px 16px",marginBottom:20,display:"flex",alignItems:"center",gap:12}}>
 <span style={{fontSize:18}}> </span>
 <div>
 <div style={{fontFamily:"'Special Gothic Expanded One',sans-serif",fontSize:13,color:"#FC4D16",letterSpacing:".03em"}}>{dueCount} touchpoint{dueCount!==1?"s":""} need your review</div>
 <div style={{fontSize:11,color:"rgba(252,77,22,.6)",marginTop:2}}>These are not set to auto-send. Open a contact to send or to switch on auto-send for future touchpoints.</div>
 </div>
 </div>
 )}
 <div className="stats">
 {priorities.map(pri=>(
 <div className="stat" key={pri}>
 <div className="stat-lbl">{pri} Priority</div>
 <div className="stat-val blu">{contacts.filter(c=>c.priority===pri).length}</div>
 </div>
 ))}
 <div className="stat"><div className="stat-lbl">Total Contacts</div><div className="stat-val">{contacts.length}</div></div>
 </div>
 {contacts.length===0?(
 <div className="empty"><div className="empty-icon"> </div><div className="empty-ttl">No contacts yet</div><p style={{fontSize:12,color:"rgba(183,217,255,.2)"}}>Add customers post-handover to start the automated contact sequence</p></div>
 ):(
 <div style={{display:"flex",flexDirection:"column",gap:8}}>
 {sorted.map(c=>{
 const due = TOUCHPOINTS.filter(tp=>getTouchpointStatus(c,tp)==="due").length;
 const upcoming = TOUCHPOINTS.filter(tp=>getTouchpointStatus(c,tp)==="upcoming").length;
 const done = TOUCHPOINTS.filter(tp=>getTouchpointStatus(c,tp)==="sent").length;
 return (
 <div key={c.id} style={{background:priorityColors[c.priority],border:`1px solid ${priorityText[c.priority]}33`,borderRadius:12,padding:"16px 18px",display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12,flexWrap:"wrap",cursor:"pointer"}}
 onClick={()=>setSelected(c.id)}>
 <div style={{flex:1,minWidth:0}}>
 <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
 <span style={{fontFamily:"'Special Gothic Expanded One',sans-serif",fontSize:13,color:"#FFF5E9"}}>{c.name||c.company}</span>
 <span style={{fontSize:9,fontWeight:700,padding:"2px 8px",borderRadius:100,background:priorityText[c.priority]+"22",color:priorityText[c.priority],textTransform:"uppercase",letterSpacing:".04em"}}>{c.priority}</span>
 {due>0&&<span style={{fontSize:9,fontWeight:700,padding:"2px 8px",borderRadius:100,background:"rgba(252,77,22,.2)",color:"#FC4D16",textTransform:"uppercase",letterSpacing:".04em"}}> {due} due</span>}
 {upcoming>0&&due===0&&<span style={{fontSize:9,fontWeight:700,padding:"2px 8px",borderRadius:100,background:"rgba(217,229,113,.1)",color:"#D9E571",textTransform:"uppercase",letterSpacing:".04em"}}> {upcoming} upcoming</span>}
 </div>
 {c.company&&c.name&&<div style={{fontSize:11,color:"rgba(183,217,255,.4)"}}>{c.company}</div>}
 {c.vehicleSpec&&<div style={{fontSize:11,color:"#B7D9FF",marginTop:3}}> {c.vehicleSpec}</div>}
 {c.handoverDate&&<div style={{fontSize:10,color:"rgba(183,217,255,.3)",marginTop:3}}>Handover: {c.handoverDate} · {done}/{TOUCHPOINTS.length} touchpoints complete</div>}
 {/* Mini sequence indicator */}
 <div style={{display:"flex",gap:4,marginTop:8}}>
 {TOUCHPOINTS.map(tp=>{
 const st = getTouchpointStatus(c,tp);
 return <div key={tp.key} title={tp.label} style={{width:8,height:8,borderRadius:"50%",background:st==="sent"?"#064E60":st==="due"?"#FC4D16":st==="upcoming"?"#D9E571":"rgba(6,78,96,.2)"}}/>;
 })}
 </div>
 </div>
 <div style={{display:"flex",gap:8,alignItems:"center"}} onClick={e=>e.stopPropagation()}>
 <select value={c.priority} onChange={e=>setContacts(cs=>cs.map(x=>x.id===c.id?{...x,priority:e.target.value}:x))}
 style={{background:"rgba(6,78,96,.3)",color:"#B7D9FF",border:"1px solid rgba(6,78,96,.6)",borderRadius:100,padding:"5px 12px",fontSize:10,fontWeight:700,fontFamily:"'DM Sans',sans-serif",cursor:"pointer",outline:"none"}}>
 {priorities.map(pri=><option key={pri} value={pri}>{pri}</option>)}
 </select>
 <button className="btn btn-ghost" style={{color:"#FFB7B8",padding:"4px 8px"}} onClick={()=>setContacts(cs=>cs.filter(x=>x.id!==c.id))}>✕</button>
 </div>
 </div>
 );
 })}
 </div>
 )}
 {showAdd&&(
 <div className="overlay">
 <div className="modal">
 <div className="mh"><span className="mt">Add Contact</span><button className="btn btn-ghost" onClick={()=>setShowAdd(false)}>✕</button></div>
 <div className="mb">
 <div className="fr"><div className="fg"><label className="fl">Name</label><input className="fi" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}/></div><div className="fg"><label className="fl">Company</label><input className="fi" value={form.company} onChange={e=>setForm(f=>({...f,company:e.target.value}))}/></div></div>
 <div className="fr"><div className="fg"><label className="fl">Email</label><input className="fi" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))}/></div><div className="fg"><label className="fl">Phone</label><input className="fi" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))}/></div></div>
 <div className="fr"><div className="fg"><label className="fl">Vehicle</label><input className="fi" value={form.vehicleSpec} onChange={e=>setForm(f=>({...f,vehicleSpec:e.target.value}))}/></div><div className="fg"><label className="fl">Handover Date</label><input className="fi" type="date" value={form.handoverDate} onChange={e=>setForm(f=>({...f,handoverDate:e.target.value}))}/></div></div>
 <div className="fr"><div className="fg"><label className="fl">Priority</label><select className="fs" value={form.priority} onChange={e=>setForm(f=>({...f,priority:e.target.value}))}>{priorities.map(pri=><option key={pri}>{pri}</option>)}</select></div><div className="fg"><label className="fl">Notes</label><input className="fi" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></div></div>
 <div className="ma"><button className="btn btn-secondary" onClick={()=>setShowAdd(false)}>Cancel</button><button className="btn btn-primary" onClick={add}>Add Contact</button></div>
 </div>
 </div>
 </div>
 )}
 </div>
 );
}
// ── DEALS SCREEN ─────────────────────────────────────────────────────────────
function DealsScreen({ cfg }) {
 const [deals, setDeals] = useState(() => loadData("deals_v3", []));
 const [selected, setSelected] = useState(null);
 const [showAdd, setShowAdd] = useState(false);
 const [filter, setFilter] = useState("All");
 const [form, setForm] = useState({customerName:"",companyName:"",customerEmail:"",vehicleSpec:"",orderDate:new Date().toISOString().split("T")[0],funder:"",notes:""});
 const phases = cfg.workflow.phases;
 useEffect(()=>saveData("deals_v3",deals),[deals]);
 // Auto-create a commission row + bump targets when the final "commission payment logged" task completes
 const triggerCommissionAndTargets = (deal) => {
 const cols = cfg.commission.columns;
 const funders = cfg.commission.funders;
 const statuses = cfg.commission.statuses;
 const rows = loadData("commission_v3", []);
 const alreadyLogged = rows.some(r=>r.dealId===deal.id);
 if(!alreadyLogged){
 const newRow = {
 id: uid(),
 dealId: deal.id,
 month: new Date().getMonth(),
 customer: deal.customerName || deal.companyName || "",
 commNumber: "",
 funder: deal.funder || funders[0] || "",
 ...Object.fromEntries(cols.map(c=>[c,""])),
 status: statuses[0] || "Pipeline",
 };
 saveData("commission_v3", [...rows, newRow]);
 }
 // Bump "Handovers" target (or first label as fallback) for the current month
 const labels = cfg.targets.labels;
 const handoverLabel = labels.find(l=>/handover/i.test(l)) || labels[0];
 if(handoverLabel){
 const targetsData = loadData("targets_v3", {
 annual: Object.fromEntries(labels.map((l,i)=>[l,cfg.targets.annual[i]||0])),
 monthly: {}
 });
 const mi = new Date().getMonth();
 const key = `${handoverLabel}_${mi}`;
 const existing = targetsData.monthly[key] || {target:Math.round((targetsData.annual[handoverLabel]||0)/12),actual:0};
 const alreadyCounted = (deal.targetsCounted||[]).includes(handoverLabel);
 if(!alreadyCounted){
 targetsData.monthly[key] = {...existing, actual:(existing.actual||0)+1};
 saveData("targets_v3", targetsData);
 }
 }
 };
 const makeTasksFromConfig = () => cfg.workflow.tasks.map(t=>({
 id:uid(), phase:t.phase, name:t.name, type:t.type, detail:t.detail,
 assignedTo:t.type==="action"?"You":"System", status:"Active", notes:"", dueDate:""
 }));
 const addDeal = () => {
 const deal = {id:uid(), ...form, tasks:makeTasksFromConfig(), createdAt:new Date().toISOString(), targetsCounted:[]};
 setDeals(d=>[...d,deal]);
 setForm({customerName:"",companyName:"",customerEmail:"",vehicleSpec:"",orderDate:new Date().toISOString().split("T")[0],funder:"",notes:""});
 setShowAdd(false);
 };
 const updateTask = (dealId, task) => {
 setDeals(ds=>ds.map(d=>{
 if(d.id!==dealId) return d;
 const updatedDeal = {...d,tasks:d.tasks.map(t=>t.id===task.id?task:t)};
 const isCommissionTask = /deal complete|commission payment logged/i.test(task.name);
 if(isCommissionTask && task.status==="Complete"){
 triggerCommissionAndTargets(updatedDeal);
 const labels = cfg.targets.labels;
 const handoverLabel = labels.find(l=>/handover/i.test(l)) || labels[0];
 updatedDeal.targetsCounted = [...(d.targetsCounted||[]), handoverLabel].filter(Boolean);
 }
 return updatedDeal;
 }));
 };
 const currentPhase = (deal) => {
 for(const ph of phases){
 const phaseTasks = (deal.tasks||[]).filter(t=>t.phase===ph);
 if(phaseTasks.some(t=>t.status!=="Complete")) return ph;
 }
 return phases[phases.length-1];
 };
 const filters = ["All",...phases,"Complete"];
 const filtered = deals.filter(d=>{
 if(filter==="All") return true;
 if(filter==="Complete") return getProgress(d.tasks)===100;
 return currentPhase(d)===filter;
 });
 if(selected){
 const deal = deals.find(d=>d.id===selected);
 if(!deal){setSelected(null);return null;}
 const progress = getProgress(deal.tasks);
 const blocked = getBlocked(deal.tasks);
 const isDone = progress===100;
 return (
 <div>
 <button className="back-btn" onClick={()=>setSelected(null)}>← Back to Deals</button>
 <div className="dhdr">
 <div className="dh-row">
 <div>
 <div className="dh-name">{deal.customerName}</div>
 <div className="dh-co">{deal.companyName}</div>
 <div className="dh-veh">{deal.vehicleSpec}</div>
 <div className="dh-meta">
 <div className="mi"><strong>Phase:</strong> {currentPhase(deal)}</div>
 <div className="mi"><strong>Order Date:</strong> {deal.orderDate}</div>
 {deal.funder&&<div className="mi"><strong>Funder:</strong> {deal.funder}</div>}
 {deal.customerEmail&&<div className="mi"><strong>Email:</strong> {deal.customerEmail}</div>}
 </div>
 </div>
 <div className="dh-right">
 <div style={{fontFamily:"'Special Gothic Expanded One',sans-serif",fontSize:32,color:isDone?"#D9E571":blocked>0?"#FFB7B8":"#B7D9FF"}}>{progress}%</div>
 <div style={{fontSize:11,color:"rgba(183,217,255,.4)"}}>{deal.tasks?.filter(t=>t.status==="Complete").length}/{deal.tasks?.length} tasks</div>
 {blocked>0&&<div style={{fontSize:11,color:"#FFB7B8",fontWeight:700}}>⚠ {blocked} blocked</div>}
 <button className="btn btn-danger btn-sm" onClick={()=>{if(window.confirm("Delete this deal?"))setDeals(ds=>ds.filter(d=>d.id!==selected));setSelected(null);}}>Delete Deal</button>
 </div>
 </div>
 <div style={{marginTop:16}}>
 <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:"rgba(183,217,255,.3)",marginBottom:6}}>
 <span>Progress</span><span>{progress}%</span>
 </div>
 <div className="prog-track" style={{height:6,borderRadius:3}}>
 <div className={`prog-fill${isDone?" done":""}`} style={{width:`${progress}%`,height:6,borderRadius:3}}/>
 </div>
 </div>
 </div>
 {phases.map(ph=>{
 const phaseTasks = (deal.tasks||[]).filter(t=>t.phase===ph);
 if(phaseTasks.length===0) return null;
 return <PhaseGroup key={ph} phaseName={ph} tasks={phaseTasks} cfg={cfg} onUpdate={t=>updateTask(deal.id,t)}/>;
 })}
 </div>
 );
 }
 return (
 <div>
 <div className="sh">
 <div><div className="sh-title"> Deals</div><div className="sh-sub">{deals.length} active deals</div></div>
 <button className="btn btn-primary" onClick={()=>setShowAdd(true)}>+ New Deal</button>
 </div>
 <div className="stats">
 <div className="stat"><div className="stat-lbl">Total Deals</div><div className="stat-val">{deals.length}</div></div>
 <div className="stat"><div className="stat-lbl">In Progress</div><div className="stat-val blu">{deals.filter(d=>getProgress(d.tasks)>0&&getProgress(d.tasks)<100).length}</div></div>
 <div className="stat"><div className="stat-lbl">Blocked</div><div className="stat-val red">{deals.filter(d=>getBlocked(d.tasks)>0).length}</div></div>
 <div className="stat"><div className="stat-lbl">Complete</div><div className="stat-val grn">{deals.filter(d=>getProgress(d.tasks)===100).length}</div></div>
 </div>
 <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:20}}>
 {filters.map(f=>(
 <button key={f} onClick={()=>setFilter(f)}
 style={{fontSize:10,fontWeight:700,padding:"5px 14px",borderRadius:100,border:"none",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",letterSpacing:".04em",textTransform:"uppercase",
 background:filter===f?"#FC4D16":"rgba(6,78,96,.25)",color:filter===f?"#fff":"rgba(183,217,255,.5)",transition:"all .15s"}}>
 {f}
 </button>
 ))}
 </div>
 {filtered.length===0?(
 <div className="empty"><div className="empty-icon"> </div><div className="empty-ttl">No deals here</div></div>
 ):(
 <div className="grid">
 {filtered.map(deal=>{
 const prog = getProgress(deal.tasks);
 const blk = getBlocked(deal.tasks);
 const done = prog===100;
 const ph = currentPhase(deal);
 return (
 <div key={deal.id} className={`card${blk>0?" blk":""}${done?" done":""}`} onClick={()=>setSelected(deal.id)}>
 <div className="c-top">
 <div>
 <div className="c-name">{deal.customerName}</div>
 <div className="c-co">{deal.companyName}</div>
 </div>
 <div style={{display:"flex",flexDirection:"column",gap:4,alignItems:"flex-end"}}>
 <span className="pill pill-phase">{ph}</span>
 {blk>0&&<span className="pill pill-multi">⚠ {blk} blocked</span>}
 </div>
 </div>
 <div className="c-veh">{deal.vehicleSpec}</div>
 <div className="prog-bar">
 <div className="prog-labels"><span>Progress</span><span>{prog}%</span></div>
 <div className="prog-track"><div className={`prog-fill${done?" done":""}`} style={{width:`${prog}%`}}/></div>
 </div>
 <div className="c-foot">
 <span className="tag tag-action">{deal.orderDate}</span>
 {deal.funder&&<span className="tag" style={{background:"rgba(183,217,255,.1)",color:"#B7D9FF"}}>{deal.funder}</span>}
 {done&&<span className="tag tag-ok">✓ Complete</span>}
 </div>
 </div>
 );
 })}
 </div>
 )}
 {showAdd&&(
 <div className="overlay">
 <div className="modal">
 <div className="mh"><span className="mt">New Deal</span><button className="btn btn-ghost" onClick={()=>setShowAdd(false)}>✕</button></div>
 <div className="mb">
 <div className="fr"><div className="fg"><label className="fl">Customer Name</label><input className="fi" value={form.customerName} onChange={e=>setForm(f=>({...f,customerName:e.target.value}))}/></div><div className="fg"><label className="fl">Company</label><input className="fi" value={form.companyName} onChange={e=>setForm(f=>({...f,companyName:e.target.value}))}/></div></div>
 <div className="fr"><div className="fg"><label className="fl">Customer Email</label><input className="fi" type="email" value={form.customerEmail} onChange={e=>setForm(f=>({...f,customerEmail:e.target.value}))}/></div><div className="fg"><label className="fl">Order Date</label><input className="fi" type="date" value={form.orderDate} onChange={e=>setForm(f=>({...f,orderDate:e.target.value}))}/></div></div>
 <div className="fg"><label className="fl">Vehicle Specification</label><input className="fi" value={form.vehicleSpec} onChange={e=>setForm(f=>({...f,vehicleSpec:e.target.value}))}/></div>
 <div className="fr"><div className="fg"><label className="fl">Funder</label><select className="fs" value={form.funder} onChange={e=>setForm(f=>({...f,funder:e.target.value}))}><option value="">Select funder...</option>{cfg.commission.funders.map(f=><option key={f}>{f}</option>)}</select></div><div className="fg"><label className="fl">Notes</label><input className="fi" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></div></div>
 <div className="ma"><button className="btn btn-secondary" onClick={()=>setShowAdd(false)}>Cancel</button><button className="btn btn-primary" disabled={!form.customerName||!form.vehicleSpec} onClick={addDeal}>Create Deal</button></div>
 </div>
 </div>
 </div>
 )}
 </div>
 );
}
// ── COMMISSION SCREEN ─────────────────────────────────────────────────────────
function CommissionScreen({ cfg }) {
 const [rows, setRows] = useState(() => loadData("commission_v3", []));
 const [openMonths, setOpenMonths] = useState({});
 const cols = cfg.commission.columns;
 const statuses = cfg.commission.statuses;
 const funders = cfg.commission.funders;
 // Re-read from storage periodically so rows auto-created by Deals appear without a manual refresh
 useEffect(()=>{
 const interval = setInterval(()=>{
 const latest = loadData("commission_v3", []);
 setRows(prev => JSON.stringify(prev)===JSON.stringify(latest) ? prev : latest);
 }, 1500);
 return () => clearInterval(interval);
 },[]);
 useEffect(()=>saveData("commission_v3",rows),[rows]);
 const makeRow = (month) => ({id:uid(),month,customer:"",commNumber:"",funder:funders[0]||"",...Object.fromEntries(cols.map(c=>[c,""])),status:statuses[0]||"Pipeline"});
 const rowTotal = row => cols.reduce((s,c)=>s+num(row[c]||0),0);
 const addRow = (month) => setRows(r=>[...r,makeRow(month)]);
 const updateRow = (id,fields) => setRows(rs=>rs.map(r=>r.id===id?{...r,...fields}:r));
 const delRow = (id) => setRows(rs=>rs.filter(r=>r.id!==id));
 const statusColors = {[statuses[0]]:"status-pipeline",[statuses[1]]:"status-confirmed",[statuses[2]]:"status-paid"};
 const statusPillClass = s => `comm-status-pill ${statusColors[s]||"status-pipeline"}`;
 const yearTotal = rows.reduce((s,r)=>s+rowTotal(r),0);
 const paidTotal = rows.filter(r=>r.status===statuses[2]).reduce((s,r)=>s+rowTotal(r),0);
 const confirmedTotal = rows.filter(r=>r.status===statuses[1]).reduce((s,r)=>s+rowTotal(r),0);
 return (
 <div>
 <div className="sh">
 <div><div className="sh-title"> Commission</div><div className="sh-sub">Full year tracker</div></div>
 </div>
 <div className="year-total-box">
 <div><div className="yt-label">Year Total</div><div className="yt-val">{fmt(yearTotal)}</div></div>
 <div><div className="yt-label">{statuses[2]||"Paid"}</div><div className="yt-val" style={{fontSize:20}}>{fmt(paidTotal)}</div></div>
 <div><div className="yt-label">{statuses[1]||"Confirmed"}</div><div className="yt-val" style={{fontSize:20}}>{fmt(confirmedTotal)}</div></div>
 <div><div className="yt-label">{statuses[0]||"Pipeline"}</div><div className="yt-val" style={{fontSize:20}}>{fmt(yearTotal-paidTotal-confirmedTotal)}</div></div>
 </div>
 <div style={{height:24}}/>
 {MONTHS.map((month,mi)=>{
 const mRows = rows.filter(r=>r.month===mi);
 const mTotal = mRows.reduce((s,r)=>s+rowTotal(r),0);
 const isOpen = openMonths[mi]!==false;
 return (
 <div className="comm-section" key={month}>
 <div className="comm-month-hdr" onClick={()=>setOpenMonths(o=>({...o,[mi]:!isOpen}))}>
 <div style={{display:"flex",alignItems:"center",gap:12}}>
 <span style={{fontSize:14}}>{isOpen?"▼":"▶"}</span>
 <span className="comm-month-name">{month}</span>
 <span className="comm-month-count">{mRows.length} deal{mRows.length!==1?"s":""}</span>
 </div>
 <span className="comm-month-total">{mTotal>0?fmt(mTotal):"—"}</span>
 </div>
 {isOpen&&(
 <div style={{background:"rgba(6,78,96,.08)",border:"1px solid rgba(6,78,96,.3)",borderTop:"none",borderRadius:"0 0 10px 10px",overflow:"hidden"}}>
 <div style={{overflowX:"auto"}}>
 <table className="comm-table">
 <thead>
 <tr>
 <th>Customer</th>
 <th>Comm #</th>
 <th>Funder</th>
 {cols.map(c=><th key={c}>{c} £</th>)}
 <th>Total</th>
 <th>Status</th>
 <th></th>
 </tr>
 </thead>
 <tbody>
 {mRows.map(r=>(
 <tr key={r.id}>
 <td>
 <input className="comm-input" value={r.customer} onChange={e=>updateRow(r.id,{customer:e.target.value})} placeholder="Customer name"/>
 {r.dealId&&<div style={{fontSize:9,color:"#D9E571",marginTop:4,fontWeight:700,textTransform:"uppercase",letterSpacing:".04em"}}>Auto from Deal</div>}
 </td>
 <td><input className="comm-input" value={r.commNumber||""} onChange={e=>updateRow(r.id,{commNumber:e.target.value})} placeholder="#"/></td>
 <td>
 <select className="comm-input" value={r.funder} onChange={e=>updateRow(r.id,{funder:e.target.value})} style={{background:"transparent",cursor:"pointer"}}>
 {funders.map(f=><option key={f}>{f}</option>)}
 </select>
 </td>
 {cols.map(c=>(
 <td key={c}><input className="comm-input" type="number" value={r[c]||""} onChange={e=>updateRow(r.id,{[c]:e.target.value})} placeholder="0.00"/></td>
 ))}
 <td style={{fontWeight:600,color:"#D9E571"}}>{rowTotal(r)>0?fmt(rowTotal(r)):"—"}</td>
 <td>
 <select className={statusPillClass(r.status)} value={r.status} onChange={e=>updateRow(r.id,{status:e.target.value})} style={{border:"none",cursor:"pointer"}}>
 {statuses.map(s=><option key={s}>{s}</option>)}
 </select>
 </td>
 <td><button className="btn btn-ghost" style={{color:"#FFB7B8",fontSize:11,padding:"2px 6px"}} onClick={()=>delRow(r.id)}>✕</button></td>
 </tr>
 ))}
 {mRows.length>0&&(
 <tr className="comm-total-row">
 <td colSpan={cols.length+3} style={{textAlign:"right",paddingRight:12}}>Month Total</td>
 <td>{fmt(mTotal)}</td>
 <td colSpan={2}></td>
 </tr>
 )}
 </tbody>
 </table>
 </div>
 <div style={{padding:"8px 12px"}}>
 <button className="add-row-btn" onClick={()=>addRow(mi)}>+ Add Deal</button>
 </div>
 </div>
 )}
 </div>
 );
 })}
 </div>
 );
}
// ── TARGETS SCREEN ────────────────────────────────────────────────────────────
function TargetsScreen({ cfg }) {
 const labels = cfg.targets.labels;
 const [targets, setTargets] = useState(() => loadData("targets_v3", {
 annual: Object.fromEntries(labels.map((l,i)=>[l,cfg.targets.annual[i]||0])),
 monthly: {}
 }));
 // Re-read from storage periodically so updates auto-triggered by Deals appear without a manual refresh
 useEffect(()=>{
 const interval = setInterval(()=>{
 const latest = loadData("targets_v3", null);
 if(latest) setTargets(prev => JSON.stringify(prev)===JSON.stringify(latest) ? prev : latest);
 }, 1500);
 return () => clearInterval(interval);
 },[]);
 useEffect(()=>saveData("targets_v3",targets),[targets]);
 const setAnnual = (label,val) => setTargets(t=>({...t,annual:{...t.annual,[label]:parseFloat(val)||0}}));
 const setMonthly = (label,month,key,val) => setTargets(t=>({...t,monthly:{...t.monthly,[`${label}_${month}`]:{...(t.monthly[`${label}_${month}`]||{}),target:t.annual[label]?Math.round(t.annual[label]/12):0,[key]:parseFloat(val)||0}}}));
 const getM = (label,month) => targets.monthly[`${label}_${month}`]||{target:Math.round((targets.annual[label]||0)/12),actual:0};
 const ragClass = (actual,target) => {
 if(!target||!actual) return "rag-none";
 const pct = actual/target;
 if(pct>=1) return "rag-green";
 if(pct>=0.8) return "rag-amber";
 return "rag-red";
 };
 return (
 <div>
 <div className="sh">
 <div><div className="sh-title"> Targets</div><div className="sh-sub">Annual & monthly performance</div></div>
 </div>
 <div className="target-grid">
 {labels.map(label=>{
 const annual = targets.annual[label]||0;
 const totalActual = MONTHS.reduce((s,_,i)=>s+(getM(label,i).actual||0),0);
 const pct = annual>0?Math.min(100,Math.round(totalActual/annual*100)):0;
 const cls = totalActual>=annual?"ahead":totalActual>=annual*.8?"ontrack":"behind";
 return (
 <div className="target-card" key={label}>
 <div className="tc-label">{label}</div>
 <div className="tc-vals">
 <span className={`tc-actual ${cls}`}>{totalActual.toLocaleString()}</span>
 <span className="tc-target">of <input className="target-input" type="number" value={annual} onChange={e=>setAnnual(label,e.target.value)} onClick={e=>e.stopPropagation()}/></span>
 </div>
 <div className="tc-bar"><div className="tc-bar-fill" style={{width:`${pct}%`,background:cls==="ahead"?"#D9E571":cls==="ontrack"?"#B7D9FF":"#FC4D16"}}/></div>
 <div style={{fontSize:10,color:"rgba(183,217,255,.3)",marginTop:8,textTransform:"uppercase",letterSpacing:".08em"}}>{pct}% of annual target</div>
 </div>
 );
 })}
 </div>
 <div style={{overflowX:"auto"}}>
 <table className="monthly-table">
 <thead>
 <tr>
 <th>Month</th>
 {labels.map(l=><th key={l} colSpan={2}>{l}</th>)}
 </tr>
 <tr>
 <th></th>
 {labels.map(l=>[<th key={l+"t"} style={{fontSize:9,color:"rgba(183,217,255,.25)"}}>Target</th>,<th key={l+"a"} style={{fontSize:9,color:"rgba(183,217,255,.25)"}}>Actual</th>])}
 </tr>
 </thead>
 <tbody>
 {MONTHS.map((month,mi)=>(
 <tr key={month}>
 <td style={{fontWeight:600}}>{month}</td>
 {labels.map(label=>{
 const m = getM(label,mi);
 return [
 <td key={label+"t"}>
 <input className="target-input" type="number" value={m.target||Math.round((targets.annual[label]||0)/12)} onChange={e=>setMonthly(label,mi,"target",e.target.value)} style={{width:70}}/>
 </td>,
 <td key={label+"a"} style={{display:"flex",alignItems:"center",gap:8}}>
 <input className="target-input" type="number" value={m.actual||""} onChange={e=>setMonthly(label,mi,"actual",e.target.value)} style={{width:70}}/>
 {m.actual>0&&<span className={`rag ${ragClass(m.actual,m.target||Math.round((targets.annual[label]||0)/12))}`}>{Math.round(m.actual/(m.target||1)*100)}%</span>}
 </td>
 ];
 })}
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </div>
 );
}
// ── SETTINGS SCREEN ───────────────────────────────────────────────────────────
function SettingsScreen({ cfg, onSave }) {
 const [tab, setTab] = useState("brand");
 const [local, setLocal] = useState(JSON.parse(JSON.stringify(cfg)));
 const [adminInput, setAdminInput] = useState("");
 const [unlocked, setUnlocked] = useState(false);
 const [saved, setSaved] = useState(false);
 const set = (section, key, val) => setLocal(c=>({...c,[section]:{...c[section],[key]:val}}));
 const handleSave = () => {
 onSave(local);
 setSaved(true);
 setTimeout(()=>setSaved(false),2000);
 };
 const settingsTabs = [
 {id:"brand",label:"Brand"},
 {id:"workflow",label:"Workflow"},
 {id:"commission",label:"Commission"},
 {id:"targets",label:"Targets"},
 {id:"email",label:"Email"},
 {id:"templates",label:"Templates"},
 {id:"integrations",label:"Integrations"},
 {id:"access",label:"Access"},
 ];
 if(!unlocked) return (
 <div>
 <div className="sh"><div><div className="sh-title"> Settings</div><div className="sh-sub">Admin access required</div></div></div>
 <div style={{maxWidth:400}}>
 <div style={{background:"rgba(6,78,96,.15)",border:"1px solid rgba(6,78,96,.4)",borderRadius:14,padding:24}}>
 <div style={{fontFamily:"'Special Gothic Expanded One',sans-serif",fontSize:14,color:"#FFF5E9",marginBottom:8,letterSpacing:".04em"}}>Enter Admin Password</div>
 <div style={{fontSize:12,color:"rgba(183,217,255,.4)",marginBottom:16}}>Settings are password protected to prevent accidental changes.</div>
 <input className="fi" type="password" value={adminInput} onChange={e=>setAdminInput(e.target.value)}
 onKeyDown={e=>e.key==="Enter"&&(adminInput===cfg.access.adminPassword?setUnlocked(true):setAdminInput(""))}
 placeholder="Admin password" autoFocus/>
 {adminInput&&adminInput!==cfg.access.adminPassword&&<div style={{fontSize:11,color:"#FFB7B8",marginTop:8}}>Incorrect password</div>}
 <button className="btn btn-primary" style={{marginTop:14,width:"100%"}} onClick={()=>adminInput===cfg.access.adminPassword?setUnlocked(true):setAdminInput("")}>
 Unlock Settings
 </button>
 </div>
 </div>
 </div>
 );
 return (
 <div>
 <div className="sh">
 <div><div className="sh-title"> Settings</div><div className="sh-sub">Configure your DRIVE.AI instance</div></div>
 <button className="btn btn-primary" onClick={handleSave}>{saved?"✓ Saved":"Save Changes"}</button>
 </div>
 <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:24}}>
 {settingsTabs.map(t=>(
 <button key={t.id} onClick={()=>setTab(t.id)}
 style={{fontSize:11,fontWeight:700,padding:"7px 16px",borderRadius:100,border:"none",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",letterSpacing:".03em",
 background:tab===t.id?"#FC4D16":"rgba(6,78,96,.25)",color:tab===t.id?"#fff":"rgba(183,217,255,.5)",transition:"all .15s"}}>
 {t.label}
 </button>
 ))}
 </div>
 {tab==="brand"&&(
 <div style={{maxWidth:600}}>
 <div className="admin-section-title">Identity</div>
 <div className="fr">
 <div className="fg"><label className="fl">App Name</label><input className="fi" value={local.brand.name} onChange={e=>set("brand","name",e.target.value)}/></div>
 <div className="fg"><label className="fl">Name Accent</label><input className="fi" value={local.brand.nameAccent} onChange={e=>set("brand","nameAccent",e.target.value)}/></div>
 </div>
 <div className="fg"><label className="fl">Tagline</label><input className="fi" value={local.brand.tagline} onChange={e=>set("brand","tagline",e.target.value)}/></div>
 <div className="admin-section-title">Colours</div>
 <div className="color-row">
 {[["primaryColor","Primary (buttons)"],["secondaryColor","Secondary (structure)"],["accentColor","Accent (nav)"],
 ["highlightColor","Highlight (positive)"],["infoColor","Info (sky tone)"],["navBg","Nav Background"],["bgColor","Page Background"]
 ].map(([key,label])=>(
 <div className="color-fg" key={key}>
 <label className="fl">{label}</label>
 <div className="color-input-row">
 <input type="color" className="color-swatch" value={local.brand[key]} onChange={e=>set("brand",key,e.target.value)}/>
 <input className="fi" value={local.brand[key]} onChange={e=>set("brand",key,e.target.value)} style={{fontFamily:"monospace",fontSize:12}}/>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}
 {tab==="workflow"&&(
 <div>
 <div className="admin-section-title">Workflow Phases</div>
 <div className="phase-list" style={{maxWidth:500}}>
 {local.workflow.phases.map((phase,i)=>(
 <div className="phase-item" key={i}>
 <span className="phase-num">{i+1}</span>
 <input className="fi" value={phase} onChange={e=>{
 const phases=[...local.workflow.phases];
 const old=phases[i];
 phases[i]=e.target.value;
 const tasks=local.workflow.tasks.map(t=>t.phase===old?{...t,phase:e.target.value}:t);
 setLocal(c=>({...c,workflow:{...c.workflow,phases,tasks}}));
 }}/>
 </div>
 ))}
 </div>
 <div className="admin-section-title" style={{marginTop:24}}>Tasks by Phase</div>
 {local.workflow.phases.map(phase=>(
 <div key={phase} style={{marginBottom:20}}>
 <div style={{fontSize:10,fontWeight:700,color:"rgba(183,217,255,.5)",textTransform:"uppercase",letterSpacing:".1em",marginBottom:8,display:"flex",alignItems:"center",gap:8}}>
 <span style={{width:3,height:12,background:"#FC4D16",borderRadius:2,display:"inline-block"}}/>
 {phase}
 </div>
 {local.workflow.tasks.filter(t=>t.phase===phase).map((task,i)=>(
 <div key={i} style={{display:"flex",gap:8,marginBottom:6,alignItems:"center"}}>
 <span style={{fontSize:9,padding:"2px 8px",borderRadius:100,background:task.type==="auto"?"rgba(217,229,113,.12)":task.type==="watch"?"rgba(183,217,255,.08)":"rgba(252,77,22,.1)",color:task.type==="auto"?"#D9E571":task.type==="watch"?"#B7D9FF":"#FC4D16",fontWeight:700,letterSpacing:".04em",textTransform:"uppercase",whiteSpace:"nowrap"}}>{task.type}</span>
 <input className="fi" value={task.name} style={{flex:1,fontSize:12}} onChange={e=>{
 const tasks=local.workflow.tasks.map(t=>t===task?{...t,name:e.target.value}:t);
 setLocal(c=>({...c,workflow:{...c.workflow,tasks}}));
 }}/>
 <button style={{background:"none",border:"none",color:"#FFB7B8",cursor:"pointer",fontSize:14,padding:"0 4px",flexShrink:0}} onClick={()=>{
 const tasks=local.workflow.tasks.filter(t=>t!==task);
 setLocal(c=>({...c,workflow:{...c.workflow,tasks}}));
 }}>✕</button>
 </div>
 ))}
 <button className="add-row-btn" style={{fontSize:10,padding:"5px 12px",marginTop:4}} onClick={()=>{
 const tasks=[...local.workflow.tasks,{phase,name:"New Task",type:"action",detail:""}];
 setLocal(c=>({...c,workflow:{...c.workflow,tasks}}));
 }}>+ Add Task to {phase}</button>
 </div>
 ))}
 </div>
 )}
 {tab==="commission"&&(
 <div style={{maxWidth:500}}>
 <div className="admin-section-title">Commission Columns</div>
 <div className="comm-col-list">
 {local.commission.columns.map((col,i)=>(
 <div className="comm-col-chip" key={i}>
 <input value={col} onChange={e=>{const cols=[...local.commission.columns];cols[i]=e.target.value;set("commission","columns",cols);}}/>
 {local.commission.columns.length>1&&<button style={{background:"none",border:"none",color:"#FFB7B8",cursor:"pointer",fontSize:12,padding:0}} onClick={()=>set("commission","columns",local.commission.columns.filter((_,j)=>j!==i))}>✕</button>}
 </div>
 ))}
 <button className="add-row-btn" onClick={()=>set("commission","columns",[...local.commission.columns,"New"])}>+ Add Column</button>
 </div>
 <div className="admin-section-title" style={{marginTop:20}}>Funders</div>
 <div className="comm-col-list">
 {local.commission.funders.map((f,i)=>(
 <div className="comm-col-chip" key={i}>
 <input value={f} onChange={e=>{const funders=[...local.commission.funders];funders[i]=e.target.value;set("commission","funders",funders);}}/>
 {local.commission.funders.length>1&&<button style={{background:"none",border:"none",color:"#FFB7B8",cursor:"pointer",fontSize:12,padding:0}} onClick={()=>set("commission","funders",local.commission.funders.filter((_,j)=>j!==i))}>✕</button>}
 </div>
 ))}
 <button className="add-row-btn" onClick={()=>set("commission","funders",[...local.commission.funders,"New"])}>+ Add Funder</button>
 </div>
 </div>
 )}
 {tab==="targets"&&(
 <div style={{maxWidth:600}}>
 <div className="admin-section-title">Target Labels & Annual Goals</div>
 {local.targets.labels.map((label,i)=>(
 <div className="fr" key={i} style={{marginBottom:10}}>
 <div className="fg" style={{margin:0}}><label className="fl">Label</label><input className="fi" value={label} onChange={e=>{const labels=[...local.targets.labels];labels[i]=e.target.value;set("targets","labels",labels);}}/></div>
 <div className="fg" style={{margin:0}}><label className="fl">Annual Target</label><input className="fi" type="number" value={local.targets.annual[i]||0} onChange={e=>{const annual=[...local.targets.annual];annual[i]=parseFloat(e.target.value)||0;set("targets","annual",annual);}}/></div>
 </div>
 ))}
 </div>
 )}
 {tab==="email"&&(
 <div style={{maxWidth:560}}>
 <div className="info-box" style={{marginBottom:20}}>
 <strong>Microsoft Outlook / Graph API</strong><br/>
 To send emails directly from the app you need to register an application in Azure Active Directory and add the credentials below. Until configured, emails are copied to your clipboard automatically.<br/><br/>
 <a href="https://docs.microsoft.com/en-us/graph/auth-register-app-v2" target="_blank" rel="noreferrer" style={{color:"#B7D9FF"}}>→ Azure App Registration Guide</a>
 </div>
 <div className="fg"><label className="fl">From Name</label><input className="fi" value={local.email?.fromName||""} onChange={e=>set("email","fromName",e.target.value)} placeholder="e.g. John Smith"/></div>
 <div className="fg"><label className="fl">From Email</label><input className="fi" value={local.email?.fromEmail||""} onChange={e=>set("email","fromEmail",e.target.value)} placeholder="john.smith@company.com"/></div>
 <div className="fg"><label className="fl">Azure Client ID</label><input className="fi" value={local.email?.msClientId||""} onChange={e=>set("email","msClientId",e.target.value)} placeholder="Azure app client ID"/></div>
 <div className="fg"><label className="fl">Azure Tenant ID</label><input className="fi" value={local.email?.msTenantId||""} onChange={e=>set("email","msTenantId",e.target.value)} placeholder="Azure tenant ID"/></div>
 <div style={{display:"flex",alignItems:"center",gap:12,marginTop:8}}>
 <input type="checkbox" checked={local.email?.configured||false} onChange={e=>set("email","configured",e.target.checked)} id="emailConfigured"/>
 <label htmlFor="emailConfigured" style={{fontSize:12,color:"rgba(183,217,255,.6)",cursor:"pointer"}}>Email credentials configured and ready</label>
 </div>
 </div>
 )}
 {tab==="templates"&&(
 <div>
 <div className="info-box" style={{marginBottom:20}}>
 Use <strong style={{color:"rgba(183,217,255,.7)"}}>{"{customerName}"}</strong>, <strong style={{color:"rgba(183,217,255,.7)"}}>{"{vehicleSpec}"}</strong>, <strong style={{color:"rgba(183,217,255,.7)"}}>{"{senderName}"}</strong> as placeholders — they are replaced automatically when the email is sent.
 </div>
 {[
 ["depositInvoice","Order Confirmation / Deposit Invoice"],
 ["buildSlot","Build Slot Confirmed"],
 ["inTransit","Vehicle In Transit"],
 ["handoverConfirmation","Handover Confirmation"],
 ["thankYou","Thank You Post Handover"],
 ["paymentChase1","Payment Chase"],
 ["rel1week","1 Week Post Handover"],
 ["rel1month","1 Month Post Handover"],
 ["rel3month","3 Months Post Handover"],
 ["rel12month","12 Months Post Handover"],
 ["rel24month","24 Months Post Handover"],
 ["rel36month","36 Months Post Handover"],
 ].map(([key,label])=>{
 const t = local.emailTemplates?.[key]||{subject:"",body:""};
 return (
 <div key={key} style={{marginBottom:20,background:"rgba(6,78,96,.1)",border:"1px solid rgba(6,78,96,.3)",borderRadius:12,padding:16}}>
 <div style={{fontFamily:"'Special Gothic Expanded One',sans-serif",fontSize:12,color:"#B7D9FF",letterSpacing:".04em",marginBottom:12}}>{label}</div>
 <div className="fg"><label className="fl">Subject</label>
 <input className="fi" value={t.subject} onChange={e=>setLocal(c=>({...c,emailTemplates:{...c.emailTemplates,[key]:{...t,subject:e.target.value}}}))}/>
 </div>
 <div className="fg"><label className="fl">Body</label>
 <textarea className="fta" style={{minHeight:120}} value={t.body} onChange={e=>setLocal(c=>({...c,emailTemplates:{...c.emailTemplates,[key]:{...t,body:e.target.value}}}))}/>
 </div>
 </div>
 );
 })}
 </div>
 )}
 {tab==="integrations"&&(
 <div style={{maxWidth:560}}>
 <div className="admin-section-title">Companies House API</div>
 <div className="info-box" style={{marginBottom:12}}>
 Your Companies House API key is stored securely on the server as an environment variable (CH_API_KEY in Vercel) — it is never entered here and never exposed to the browser. Searches run through the app's own <code>/api/companies-house</code> endpoint.
 </div>
 <div className="admin-section-title" style={{marginTop:20}}>Target Postcodes for Auto-Discovery</div>
 <div className="info-box" style={{marginBottom:12}}>Used to filter O-Licence Watch entries in Prospects. Companies House search results are not automatically filtered by these.</div>
 <div className="comm-col-list">
 {(local.prospecting?.targetPostcodes||[]).map((pc,i)=>(
 <div className="comm-col-chip" key={i}>
 <input value={pc} style={{width:60}} onChange={e=>{const pcs=[...(local.prospecting?.targetPostcodes||[])];pcs[i]=e.target.value.toUpperCase();setLocal(c=>({...c,prospecting:{...c.prospecting,targetPostcodes:pcs}}));}}/>
 <button style={{background:"none",border:"none",color:"#FFB7B8",cursor:"pointer",fontSize:12,padding:0}} onClick={()=>{const pcs=(local.prospecting?.targetPostcodes||[]).filter((_,j)=>j!==i);setLocal(c=>({...c,prospecting:{...c.prospecting,targetPostcodes:pcs}}));}}>✕</button>
 </div>
 ))}
 <button className="add-row-btn" onClick={()=>setLocal(c=>({...c,prospecting:{...c.prospecting,targetPostcodes:[...(c.prospecting?.targetPostcodes||[]),""]}}))}>+ Add Postcode</button>
 </div>
 </div>
 )}
 {tab==="access"&&(
 <div style={{maxWidth:400}}>
 <div className="admin-section-title">Passwords</div>
 <div className="fg"><label className="fl">App Password</label><input className="fi" type="password" value={local.access.appPassword} onChange={e=>set("access","appPassword",e.target.value)}/></div>
 <div className="fg"><label className="fl">Admin Password</label><input className="fi" type="password" value={local.access.adminPassword} onChange={e=>set("access","adminPassword",e.target.value)}/></div>
 <div className="info-box" style={{marginTop:16}}><strong>Note:</strong> Save changes before navigating away. Write down new passwords before saving.</div>
 <div className="admin-section-title" style={{marginTop:20}}>Reset</div>
 <button className="btn btn-danger" onClick={()=>{if(window.confirm("Reset ALL settings and data to defaults?"))onSave(DEFAULT_CONFIG);}}>Reset to Factory Defaults</button>
 </div>
 )}
 </div>
 );
}
// ── LOGIN SCREEN ──────────────────────────────────────────────────────────────
function LoginScreen({ cfg, onLogin }) {
 const [input, setInput] = useState("");
 const [error, setError] = useState(false);
 const [shake, setShake] = useState(false);
 const p = cfg.brand.primaryColor;
 const a = cfg.brand.accentColor;
 const attempt = () => {
 if(input === cfg.access.appPassword) { onLogin(); }
 else { setError(true); setShake(true); setTimeout(()=>setShake(false),500); }
 };
 return (
 <div style={{minHeight:"100vh",background:"#FFF5E9",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
 <div style={{background:"#fff",border:`1px solid ${a}1a`,borderRadius:20,padding:48,width:"100%",maxWidth:400,textAlign:"center",boxShadow:`0 8px 40px ${a}14`,animation:shake?"shake .5s":"none"}}>
 <style>{`@keyframes shake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-8px)}40%,80%{transform:translateX(8px)}}`}</style>
 <svg height={72} viewBox="0 0 747.1 747.1" xmlns="http://www.w3.org/2000/svg" style={{marginBottom:4}}>
 <circle fill={cfg.brand.infoColor} cx="373.55" cy="373.55" r="373.55"/>
 <path fill={a} d="M325.76,352.87v-62.41c0-15.2-5.27-22.29-17.63-22.29-13.58,0-19.25,7.5-19.25,22.7v62.01h-33.64v-112.47h31.41v11.96h.4c7.1-11.55,17.03-15.8,30.6-15.8,20.26,0,41.74,11.35,41.74,44.58v71.74h-33.64Z"/>
 <path fill={a} d="M454.8,283.77c-4.86-10.54-13.57-15.8-25.13-15.8-15.4,0-25.94,12.77-25.94,28.57s11.15,28.58,26.75,28.58c11.35,0,19.25-5.27,24.31-15h34.25c-6.69,28.17-30.81,46.41-59.58,46.41-33.24,0-59.58-26.95-59.58-60.19s26.54-59.78,58.97-59.78c29.59,0,53.71,18.64,59.99,47.21h-34.05Z"/>
 <polygon fill={a} points="288.88 483.82 288.88 364.38 255.24 364.38 255.24 514.34 288.88 514.34 300.14 514.34 300.14 483.82 288.88 483.82"/>
 <path fill={a} d="M395.84,514.34v-12.16h-.41c-4.87,9.93-18.24,16.01-33.03,16.01-33.23,0-56.33-26.14-56.33-60.18s24.11-59.98,56.33-59.98c13.78,0,26.55,5.47,33.03,15.8h.41v-11.96h33.63v112.47h-33.63ZM395.84,458c0-15.8-12.77-28.57-28.58-28.57s-27.56,12.77-27.56,28.98,12.36,28.37,27.96,28.37,28.17-12.57,28.17-28.78Z"/>
 <circle fill={a} cx="468.8" cy="495.13" r="23.06" opacity="0.5"/>
 <path fill={a} d="M560.07,563.89H187.03V190.85h373.04v373.04ZM213.95,536.97h319.2V217.77H213.95v319.2Z"/>
 </svg>
 <div style={{marginTop:16,marginBottom:4,fontFamily:"'Special Gothic Expanded One',sans-serif",fontSize:24,color:a,letterSpacing:".06em"}}>
 {cfg.brand.name}<span style={{color:p}}>{cfg.brand.nameAccent}</span>
 </div>
 <div style={{fontSize:11,color:"#9ca3af",marginBottom:32,letterSpacing:".12em",textTransform:"uppercase",fontWeight:600}}>{cfg.brand.tagline}</div>
 <input type="password" placeholder="Enter access code" value={input}
 onChange={e=>{setInput(e.target.value);setError(false);}}
 onKeyDown={e=>e.key==="Enter"&&attempt()}
 style={{width:"100%",background:"#FFF5E9",border:`2px solid ${error?a:"rgba(119,23,53,.2)"}`,borderRadius:100,padding:"13px 20px",fontFamily:"'DM Sans',sans-serif",fontSize:14,color:a,outline:"none",marginBottom:12,textAlign:"center",letterSpacing:".1em"}}
 autoFocus/>
 {error&&<div style={{fontSize:12,color:a,marginBottom:12,fontWeight:600}}>Incorrect access code</div>}
 <button onClick={attempt}
 style={{width:"100%",background:a,color:"#fff",border:"none",borderRadius:100,padding:14,fontFamily:"'Special Gothic Expanded One',sans-serif",fontSize:12,fontWeight:700,cursor:"pointer",letterSpacing:".06em"}}>
 ACCESS {cfg.brand.name.toUpperCase()}{cfg.brand.nameAccent.toUpperCase()}
 </button>
 <div style={{fontSize:10,color:"#d1b8c0",marginTop:24,letterSpacing:".08em",textTransform:"uppercase"}}>Powered by DRIVE.AI · NCLA Consult Limited</div>
 </div>
 </div>
 );
}
// ── MAIN APP ──────────────────────────────────────────────────────────────────
export default function App() {
 const [cfg, setCfg] = useState(() => loadData("drive_config_v3", null));
 const [authed, setAuthed] = useState(false);
 const [tab, setTab] = useState("deals");
 const saveConfig = (newCfg) => {
 setCfg(newCfg);
 saveData("drive_config_v3", newCfg);
 };
 if(!cfg) return <SetupWizard onComplete={saveConfig}/>;
 const activeCfg = cfg;
 if(!authed) return (
 <>
 <AppStyles cfg={activeCfg}/>
 <LoginScreen cfg={activeCfg} onLogin={()=>setAuthed(true)}/>
 </>
 );
 const tabs = [
 {id:"prospects",label:" Prospect"},
 {id:"deals",label:" Deals"},
 {id:"relationships",label:" Relationships"},
 {id:"commission",label:" Commission"},
 {id:"targets",label:" Targets"},
 {id:"settings",label:" Settings"},
 ];
 const totalDeals = loadData("deals_v3",[]).length;
 const totalProspects = loadData("prospects_v2",[]).length;
 return (
 <>
 <AppStyles cfg={activeCfg}/>
 <div className="app">
 <nav className="nav">
 <div className="nav-left">
 <BrandLogo cfg={activeCfg} size={40}/>
 <div className="nav-product">
 <div><span className="nav-name">{activeCfg.brand.name}</span><span className="nav-accent">{activeCfg.brand.nameAccent}</span></div>
 <div className="nav-tagline">{activeCfg.brand.tagline}</div>
 </div>
 </div>
 <div className="nav-right">
 <div className="nav-stat"><span>Deals</span><strong>{totalDeals}</strong></div>
 <div className="nav-stat"><span>Prospects</span><strong>{totalProspects}</strong></div>
 </div>
 </nav>
 <div className="app-tabs">
 {tabs.map(t=>(
 <button key={t.id} className={`app-tab${tab===t.id?" active":""}`} onClick={()=>setTab(t.id)}>{t.label}</button>
 ))}
 </div>
 <main className="main">
 {tab==="prospects"&&<ProspectsScreen cfg={activeCfg}/>}
 {tab==="deals"&&<DealsScreen cfg={activeCfg}/>}
 {tab==="relationships"&&<RelationshipsScreen cfg={activeCfg}/>}
 {tab==="commission"&&<CommissionScreen cfg={activeCfg}/>}
 {tab==="targets"&&<TargetsScreen cfg={activeCfg}/>}
 {tab==="settings"&&<SettingsScreen cfg={activeCfg} onSave={saveConfig}/>}
 </main>
 </div>
 </>
 );
}
